import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"
import dayjs, { Dayjs } from "dayjs"
import {
  Button,
  Checkbox,
  message,
  Modal,
  Input,
  DatePicker,
  TimePicker,
  Dropdown,
  Row,
  Col,
  Tag,
  Steps,
  Tabs,
  Skeleton,
  Tooltip,
} from "antd"
import type { MenuProps } from "antd"
import styled from "styled-components"
import { ExclamationCircleTwoTone, DownOutlined, InfoCircleOutlined } from "@ant-design/icons"
import { useHistory } from "react-router"
import { Link } from "react-router-dom"
import { useStoreState, StateMapper } from "easy-peasy"
import { AppDataStore } from "../appData/types"
import {
  download,
  gen,
  getActingUserRequestFields,
  getMeetDetailEditability,
  IClub,
  IMeet,
  ISeason,
  swimminglyApi,
} from "./utils"
import { parseIsoToLocal, toIsoStringUTC } from "./utils/TimeHelper"
import { screenSizes } from "../styles/GlobalStyles"
import { updateSeasonMeetArrayListener } from "./MeetSchedulePageNew"
import { useFeatureAccess } from "./FeatureAccessContext"
import { FeatureKey } from "../constants/featureAccess"
import RegularMeetCourseModal from "./UpdateMeetCourseTraditional"
import VirtualMeetCourseModal from "./UpdateMeetCourseVirtual"
import UpdateMeetCourseMyMeet from "./UpdateMeetCourseMyMeet"
import MeetOverviewTab from "./MeetOverviewTab"
import MeetEntryButton from "./MeetEntryButton"
import MeetDeclarationsTab from "./MeetDeclarationsTab"
import {
  formatSwimmerNumber,
  deriveDeclarationStatus,
  formatDeclarationAudit,
  getPdfAuditLines,
} from "./MeetDeclarationsUtils"
import MeetEventOrderTab from "./MeetEventOrderTab"
import MeetActivityLogTab from "./MeetActivityLogTab"
import ExportResultsModal from "./ExportResultsModal"
import ImportEntriesModal from "./ImportSd3Modals/ImportEntriesModal"
import ImportResultsModal from "./ImportSd3Modals/ImportResultsModal"
import SwimMeetReportStudio from "./SwimMeetReportStudio"
import ExportEntriesModal from "./ExportEntriesModal"
import UpdateMeetHomeClubTraditional from "./UpdateMeetHomeClubTraditional"
import UpdateMeetParticipatingClubsTraditional from "./UpdateMeetParticipatingClubsTraditional"
import UpdateMeetParticipatingClubsVirtual from "./UpdateMeetParticipatingClubsVirtual"
import type { TMeetEvent } from "./ViewMeetLineUp"
import generateHeatSheetPDF from "./reports/GenerateHeatSheetPdf"
import resetPageScroll from "./utils/resetScroll"

import {
  faFile,
  faFileCircleCheck,
  faCalendar,
  faFileCertificate,
  faFileChartColumn,
  faCircleCheck,
  faCircleXmark,
  faClock,
  faLocationDot,
  faLock,
  faTrash,
  faUsers,
  faWaterLadder,
  faUser,
  faBadgeSheriff,
  faEye,
  faFileImport,
  faCopy,
  faPenToSquare,
} from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

const StyledModal = styled(Modal)`
  max-width: 80%;
  min-width: 900px;

  /* container for Antd Row/Col */
  .modal-body {
    padding: 8px 6px;
  }

  /* brand styling for status steps */
  .meet-status-steps .ant-steps-item-process .ant-steps-item-icon {
    background-color: var(--primaryblue);
    border-color: var(--primaryblue);
  }

  .meet-status-steps .ant-steps-item-process .ant-steps-icon {
    color: var(--white);
  }

  .meet-status-steps .ant-steps-item-finish .ant-steps-item-icon {
    border-color: var(--primaryblue);
    color: var(--primaryblue);
  }

  .meet-status-steps.ant-steps-horizontal .ant-steps-item {
    flex: 1;
    margin-right: 0;
  }

  .meet-status-steps .ant-steps-item-container {
    width: 100%;
  }

  .item {
    text-align: center;
    height: 50px;
  }

  div.item {
    display: flex;
    align-content: center;
    align-items: center;
    justify-content: center;
  }

  /* style Antd Buttons inside .shadowed.item for consistent look */
  .shadowed.item {
    padding: 0; /* let .ant-btn fill the area */
  }
  .shadowed.item .ant-btn,
  .shadowed.item.ant-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 50px;
    padding: 0 14px;
    border-radius: 6px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
    border: 1px solid rgba(0, 0, 0, 0.06);
    text-transform: none; /* sentence case */
    font-weight: 600;
    white-space: normal; /* allow text to wrap */
  }
  .shadowed.item .ant-btn span {
    white-space: normal;
  }

  /* gradient styling for primary workflow buttons */
  .primary-gradient-button.ant-btn-primary {
    background: linear-gradient(90deg, #0f7ad1 0%, #18a0fb 100%);
    border-color: #0f7ad1;
    color: #ffffff;
  }

  .primary-gradient-button.ant-btn-primary:hover,
  .primary-gradient-button.ant-btn-primary:focus {
    background: linear-gradient(90deg, #0c63aa 0%, #0f7ad1 100%);
    border-color: #0c63aa;
    color: #ffffff;
  }

  /* elevated styling for Swim Meet Report Studio button */
  .report-studio-button.ant-btn {
    background: linear-gradient(90deg, #101242 0%, #18236b 100%);
    color: #ffffff;
    border-color: #0b0e3a;
    font-weight: 700;
    letter-spacing: 0.02em;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.18);
    height: 56px; /* slightly taller for emphasis */
    font-size: 14px;
  }

  .report-studio-button.ant-btn:hover,
  .report-studio-button.ant-btn:focus {
    background: linear-gradient(90deg, #18236b 0%, #22358d 100%);
    color: #ffffff;
    border-color: #18236b;
  }

  .report-studio-button.ant-btn span {
    display: inline-block;
  }

  .meet-name-display {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .meet-inline-edit-display {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    min-width: 0;
  }

  .meet-inline-edit-display-row {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .meet-inline-edit-label {
    font-weight: 400;
  }

  .meet-inline-edit-value {
    font-weight: 600;
  }

  .meet-inline-edit-trigger.ant-btn {
    opacity: 0;
    pointer-events: none;
    transition: opacity 120ms ease;
    padding-inline: 0;
    height: auto;
  }

  .meet-inline-edit-display:hover .meet-inline-edit-trigger.ant-btn,
  .meet-inline-edit-display:focus-within .meet-inline-edit-trigger.ant-btn,
  .meet-inline-edit-display.meet-inline-edit-visible .meet-inline-edit-trigger.ant-btn {
    opacity: 1;
    pointer-events: auto;
  }

  .meet-inline-editing {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 4px;
  }

  .meet-inline-editing-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }

  .meet-inline-edit-actions {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .desktop-only {
    display: inline-flex;
  }

  .mobile-only {
    display: none;
  }

  .meet-title-primary {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    column-gap: 8px;
    row-gap: 8px;
    min-width: 0;
  }

  .meet-title-label {
    white-space: nowrap;
    flex-shrink: 0;
  }

  .meet-name-editing {
    display: flex;
    align-items: center;
    min-width: 0;
    width: 100%;
  }

  .meet-name-edit-field {
    position: relative;
    flex: 1 1 320px;
    min-width: 0;
    width: 100%;
  }

  .meet-name-edit-input {
    width: 100%;
    max-width: 100%;
  }

  .meet-name-edit-input.ant-input {
    padding-right: 76px;
  }

  .meet-name-edit-actions {
    position: absolute;
    top: 50%;
    right: 6px;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .meet-name-edit-trigger.ant-btn {
    opacity: 0;
    pointer-events: none;
    transition: opacity 120ms ease;
    padding-inline: 0;
    height: auto;
  }

  .meet-name-display:hover .meet-name-edit-trigger.ant-btn,
  .meet-name-display:focus-within .meet-name-edit-trigger.ant-btn,
  .meet-name-display.meet-name-edit-visible .meet-name-edit-trigger.ant-btn {
    opacity: 1;
    pointer-events: auto;
  }

  @media (hover: none) {
    .meet-name-edit-trigger.ant-btn {
      opacity: 0;
      pointer-events: none;
    }

    .meet-name-display.meet-name-edit-visible .meet-name-edit-trigger.ant-btn {
      opacity: 1;
      pointer-events: auto;
    }

    .meet-inline-edit-trigger.ant-btn {
      opacity: 0;
      pointer-events: none;
    }

    .meet-inline-edit-display.meet-inline-edit-visible .meet-inline-edit-trigger.ant-btn {
      opacity: 1;
      pointer-events: auto;
    }
  }

  @media (max-width: 768px) {
    .desktop-only {
      display: none;
    }

    .mobile-only {
      display: flex;
    }

    .meet-title-primary {
      width: 100%;
    }

    .meet-name-editing {
      min-width: 0;
      align-items: center;
    }

    .meet-name-edit-field {
      flex: 1 1 160px;
      min-width: 0;
    }

    .meet-name-edit-actions {
      right: 4px;
    }

    .meet-name-edit-panel {
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 12px;
      margin-bottom: 12px;
      border: 1px solid rgba(0, 0, 0, 0.08);
      border-radius: 10px;
      background: #fafafa;
    }
  }

  /* primary variants a bit stronger */
  .shadowed.item .ant-btn.ant-btn-primary,
  .shadowed.item.ant-btn.ant-btn-primary {
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.06);
  }
  /* icon spacing */
  .shadowed.item .fa-icon,
  .shadowed.item .svg-inline--fa {
    margin-right: 8px;
    font-size: 1.05rem;
  }
  /* fallback for plain links inside .shadowed - look like buttons */
  .shadowed.item a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 50px;
    padding: 0 14px;
    border-radius: 6px;
    text-decoration: none;
    color: inherit;
  }

  /* inline layout for primary action buttons + actions menu */
  .primary-actions-inline {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }

  .primary-actions-inline > * {
    margin-right: 10px;
  }

  .primary-actions-inline > *:last-child {
    margin-right: 0;
  }

  .ant-modal-content {
    max-height: calc(100vh - 80px);
    display: flex;
    flex-direction: column;
  }

  .ant-modal-body {
    flex: 1;
    overflow: auto;
  }

  .ant-modal-footer {
    background-color: #f5f5f5;
    /* Extend footer background to cover modal content padding */
    margin: 0 -24px -24px;
    padding: 16px 24px;
    border-radius: 0 0 8px 8px;
  }

  .shadowed {
    box-shadow: 0 1px 2px 2px rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 5px;
    padding: 5px;
  }

  .declarations-row-attending td {
    background-color: #d9f7be; /* slightly darker green */
  }

  .declarations-row-not-attending td {
    background-color: #f5f5f5; /* light gray */
  }

  /* Ensure status-based row colors remain on hover for this table */
  .meet-declarations-table .ant-table-tbody > tr.declarations-row-attending:hover > td {
    background-color: #d9f7be !important;
  }

  .meet-declarations-table .ant-table-tbody > tr.declarations-row-not-attending:hover > td {
    background-color: #f5f5f5 !important;
  }

  .meet-actions-skeleton-overview-card {
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 12px;
  }

  .meet-actions-skeleton-overview-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 10px;
  }

  .meet-actions-skeleton-tab-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }

  .meet-actions-skeleton-tab-list {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    flex: 1;
  }

  .meet-actions-skeleton-content-card {
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 8px;
    padding: 14px;
    background: #fff;
  }

  @media (max-width: 1200px) {
    .meet-actions-skeleton-overview-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  @media (max-width: ${screenSizes.small}px) {
    .meet-actions-skeleton-overview-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    min-width: 0;
    max-width: 97%;
  }
`

export interface IClubSeasonInformation {
  club: IClub
  season: ISeason
  clubSeasonId: number
  startDate?: string
  endDate?: string
  seasonName?: string
  league?: string | null
  entryStatusInMeet: null | 0 | 1 | 2
  entryStatusDescription?: string
  entriesCount?: number
  confirmLineup?: boolean
  heatSheetAccepted?: boolean
}

export interface IMeetPdfStatus {
  labelsExists: boolean
  resultsExists: boolean
}

interface IDeclarationsReportEvent {
  meetEventOrderId: number
  eventNumber: number
  distance: string
  age_group: string
  eventGender: "Boys" | "Girls" | "Mixed"
  race_type: number
  enteredInEvent: 0 | 1 | null
  stroke: string
  isRelay: 0 | 1 | null
  relayAvailable?: 0 | 1 | null
  best_time_at_merge: number | null
  bestTime: number | null
  isConverted: 0 | 1 | null
  minAge: number
  maxAge: number
}

export interface IDeclarationsReportSwimmer {
  swimmerId: number
  first_name: string
  last_name: string
  middle_initials: string | null
  swimmerNumber: number | null
  leagueAge: number
  date_of_birth: string
  removedFromMeet: 0 | 1 | null
  confirmedForMeet: 0 | 1 | null
  gender: "Boy" | "Girl"
  comments?: string | null
   lastDeclarationStatus?: "attending" | "notAttending" | null
   lastDeclarationAt?: string | null
   lastDeclarationActorUserId?: number | null
    lastDeclarationActorName?: string | null
   lastDeclarationActorEmail?: string | null
   lastDeclarationActorIsParent?: boolean | null
  elligibleEvents: IDeclarationsReportEvent[]
}

interface IMeetRemovalPreview {
  meet: {
    meetId: number
    meetName: string
    meetDate: string
    hostClubName: string | null
    isRemoved: boolean
    removedAt?: string | null
    removedByNameSnapshot?: string | null
    removedByEmailSnapshot?: string | null
    removedByClubNameSnapshot?: string | null
  }
  impact: {
    teamCount: number
    entryCount: number
    clubsWithEntriesCount: number
    confirmLineupCount: number
    heatSheetAcceptedCount: number
    activeClubs: Array<{
      clubId: number
      clubName: string
      entryCount: number
      confirmLineup: boolean
      heatSheetAccepted: boolean
    }>
  }
  permissions: {
    canDelete: boolean
    reason: string | null
  }
}

interface IMeetEntriesDeadlineImpactClub {
  clubId: number
  clubNameShort: string | null
  clubNameLong: string | null
  currentMeetDeclarationsCloseDate: string | null
  nextMeetDeclarationsCloseDate: string | null
  willClamp: boolean
}

interface IMeetEntriesDeadlineImpactPreview {
  meetEntriesDeadline: string | null
  impactedClubCount: number
  clubs: IMeetEntriesDeadlineImpactClub[]
}

export default function MeetActionsModal({
  meetId,
  visible,
  setVisible,
  initialActiveTab = null,
  refreshData,
  hideEditEntriesButton = false,
  hideViewHeatSheetButton = false,
}: {
  meetId: number
  visible: boolean
  setVisible: (newVisible: boolean) => void
  initialActiveTab?: "reportStudio" | "declarations" | "eventOrder" | "activityLog" | null
  refreshData: () => void
  hideEditEntriesButton?: boolean
  hideViewHeatSheetButton?: boolean
}): JSX.Element {
  const user = useStoreState((state: StateMapper<AppDataStore>) => state.user)
  const aliasedUser = useStoreState(
    (state: StateMapper<AppDataStore>) => state.aliasedUser,
  )
  const impersonateClub = useStoreState(
    (state: StateMapper<AppDataStore>) => state.aliasedClub,
  )
  const actingUser = aliasedUser || user
  const { isFeatureLocked, openLockedModal } = useFeatureAccess()
  const [loading, setLoading] = useState(false)
  const [meetName, setMeetName] = useState("")
  const [isRenamingMeet, setIsRenamingMeet] = useState(false)
  const [renameMeetSaving, setRenameMeetSaving] = useState(false)
  const [renameMeetValue, setRenameMeetValue] = useState("")
  const [showRenameTrigger, setShowRenameTrigger] = useState(false)
  const [showEntryLimitsTrigger, setShowEntryLimitsTrigger] = useState(false)
  const [isEditingEntryLimits, setIsEditingEntryLimits] = useState(false)
  const [updateEntryLimitsSaving, setUpdateEntryLimitsSaving] = useState(false)
  const [showDateTimeTrigger, setShowDateTimeTrigger] = useState(false)
  const [showMeetEntriesDeadlineTrigger, setShowMeetEntriesDeadlineTrigger] = useState(false)
  const [isEditingMeetDateTime, setIsEditingMeetDateTime] = useState(false)
  const [isMeetDatePickerOpen, setIsMeetDatePickerOpen] = useState(false)
  const [isMeetTimePickerOpen, setIsMeetTimePickerOpen] = useState(false)
  const [updateMeetDateTimeSaving, setUpdateMeetDateTimeSaving] = useState(false)
  const [editMeetDateValue, setEditMeetDateValue] = useState<Dayjs | null>(null)
  const [editMeetEndDateValue, setEditMeetEndDateValue] = useState<Dayjs | null>(null)
  const [editMeetTimeValue, setEditMeetTimeValue] = useState<Dayjs | null>(null)
  const [meetCreatorClubId, setMeetCreatorClubId] = useState<number | null>(null)
  const [meetEntriesDeadline, setMeetEntriesDeadline] = useState<string | null>(null)
  const [autoMarkHeatSheetReadyAfterDeadline, setAutoMarkHeatSheetReadyAfterDeadline] = useState(false)
  const [isEditingMeetEntriesDeadline, setIsEditingMeetEntriesDeadline] = useState(false)
  const [editMeetEntriesDeadlineValue, setEditMeetEntriesDeadlineValue] = useState<Dayjs | null>(null)
  const [editAutoMarkHeatSheetReadyAfterDeadline, setEditAutoMarkHeatSheetReadyAfterDeadline] = useState(false)
  const [updateMeetEntriesDeadlineSaving, setUpdateMeetEntriesDeadlineSaving] = useState(false)
  const [meetEntriesDeadlineConfirmationVisible, setMeetEntriesDeadlineConfirmationVisible] =
    useState(false)
  const [meetEntriesDeadlineImpactPreview, setMeetEntriesDeadlineImpactPreview] =
    useState<IMeetEntriesDeadlineImpactPreview | null>(null)
  const [meetEntriesDeadlineInfoVisible, setMeetEntriesDeadlineInfoVisible] = useState(false)
  const [updatingHeatSheetReadyClubId, setUpdatingHeatSheetReadyClubId] = useState<number | null>(null)
  const meetNameDisplayRef = useRef<HTMLSpanElement | null>(null)
  const entryLimitsAreaRef = useRef<HTMLSpanElement | null>(null)
  const meetDateTimeAreaRef = useRef<HTMLDivElement | null>(null)
  const meetEntriesDeadlineAreaRef = useRef<HTMLDivElement | null>(null)
  const [isVirtualMeet, setIsVirtualMeet] = useState(false)
  const [meetStatus, setMeetStatus] = useState<0 | 1 | 2 | 3 | null>(null)
  const [course, setCourse] = useState<"SCY" | "SCM" | "LCM">("SCY")
  const [virtualCourse, setVirtualCourse] =
    useState<"SCY" | "SCM" | "LCM" | null>(null)
  const [unitOfMeasure, setUnitOfMeasure] = useState<number | null>(null)
  const [meetLocation, setMeetLocation] = useState<string | null>(null)
  const [meetClubId, setMeetClubId] = useState<number | null>(null)
  const [virtualMeetOrganizerClubId, setVirtualMeetOrganizerClubId] = useState<number | null>(null)
  const [homeClubId, setHomeClubId] = useState<number>(-1)
  const [virtualMeetId, setVirtualMeetId] = useState<number | null>(null)
  const [virtualMeetHasResults, setVirtualMeetHasResults] = useState(false)
  const [meetStartDate, setMeetStartDate] = useState(dayjs())
  const [meetEndDate, setMeetEndDate] = useState(dayjs())
  const [meetTime, setMeetTime] = useState<string | null>(null)
  const [individualEntryLimit, setIndividualEntryLimit] = useState<
    number | null
  >(null)
  const [relayEntryLimit, setRelayEntryLimit] = useState<number | null>(null)
  const [totalEntryLimit, setTotalEntryLimit] = useState<number | null>(null)
  const [editHasIndividualEntryLimit, setEditHasIndividualEntryLimit] =
    useState(false)
  const [editHasRelayEntryLimit, setEditHasRelayEntryLimit] = useState(false)
  const [editHasTotalEntryLimit, setEditHasTotalEntryLimit] = useState(false)
  const [editIndividualEntryLimit, setEditIndividualEntryLimit] = useState<
    number | null
  >(null)
  const [editRelayEntryLimit, setEditRelayEntryLimit] = useState<number | null>(null)
  const [editTotalEntryLimit, setEditTotalEntryLimit] = useState<number | null>(null)
  const [eventOrderName, setEventOrderName] = useState<string | null>(null)
  const [individualScoringSummary, setIndividualScoringSummary] = useState<string | null>(null)
  const [relayScoringSummary, setRelayScoringSummary] = useState<string | null>(null)
  const [numClubsInMeet, setNumClubsInMeet] = useState(0)
  const [meetClubSeasons, setMeetClubSeasons] = useState<
    IClubSeasonInformation[]
  >([])
  const [meetPdfStatus, setMeetPdfStatus] = useState<IMeetPdfStatus>()
  const [thisClubSeason, setThisClubSeason] =
    useState<IClubSeasonInformation | null>(null)
  const [meetLineupConfirmationVisible, setMeetLineupConfirmationVisible] =
    useState(false)
  const [updateTraditionalCourseVisible, setUpdateTraditionalCourseVisible] =
    useState(false)
  const [updateMyMeetCourseVisible, setUpdateMyMeetCourseVisible] =
    useState(false)
  const [updateHostClubVisible, setUpdateHostClubVisible] = useState(false)
  const [updateParticipatingClubsVisible, setUpdateParticipatingClubsVisible] =
    useState(false)
  const [updateVirtualCourseVisible, setUpdateVirtualCourseVisible] =
    useState(false)
  const [triggerRefresh, setTriggerRefresh] = useState(0)
  const [exportResultsVisible, setExportResultsVisible] = useState(false)
  const [importEntriesVisible, setImportEntriesVisible] = useState(false)
  const [importResultsVisible, setImportResultsVisible] = useState(false)
  const [sd3ImportDeactivated, setSd3ImportDeactivated] = useState(false)
  const [meetResultSource, setMeetResultSource] = useState<'fileImport' | 'scorekeeper' | 'testGenerated' | null>(null)
  const [activeTab, setActiveTab] = useState<
    "reportStudio" | "declarations" | "eventOrder" | "activityLog"
  >("reportStudio")
  const [meetEvents, setMeetEvents] = useState<TMeetEvent[]>([])
  const [eventOrderLoading, setEventOrderLoading] = useState(false)
  const [meetInfoLoading, setMeetInfoLoading] = useState(false)
  const [meetDeclarationsCloseDate, setMeetDeclarationsCloseDate] =
    useState<string | null>(null)
  const [supportsDeclarations, setSupportsDeclarations] = useState(false)
  const [parentEntries, setParentEntries] = useState(false)
  const [parentAllowIndividualEntries, setParentAllowIndividualEntries] =
    useState(false)
  const [parentAllowRelayAvailability, setParentAllowRelayAvailability] =
    useState(false)
  const [parentAllowComments, setParentAllowComments] = useState(true)
  const [declarationsReportLoading, setDeclarationsReportLoading] =
    useState(false)
  const [declarationsReportSwimmers, setDeclarationsReportSwimmers] =
    useState<IDeclarationsReportSwimmer[]>([])
  const [exportEntriesModalVisible, setExportEntriesModalVisible] =
    useState(false)
  const [exportSelectedClubIds, setExportSelectedClubIds] = useState<
    Array<number | string>
  >([])
  const [meetSessionContext, setMeetSessionContext] = useState<{
    sessionType: "timedFinals" | "prelims" | "finals"
    sessionOrder: number
    totalSessions: number
    multiSessionMeetId: number
    seriesName: string
  } | null>(null)
  const [meetDivisions, setMeetDivisions] = useState<{
    divisionId: number
    divisionNumber: number
    divisionName: string
    clubIds: number[]
  }[]>([])
  const inlineCreateReportHandlerRef = useRef<(() => void | Promise<void>) | null>(null)
  const [inlineCreateReportLabel, setInlineCreateReportLabel] = useState<string | null>(null)

  interface IMeetPool {
    poolId: number | null
    pool_name: string | null
    description: string | null
    course_label: "SCY" | "SCM" | "LCM" | null
    poolLengthInCourseUnits: number | null
    address: string | null
    numLanes: number | null
  }

  const [meetPool, setMeetPool] = useState<IMeetPool | null>(null)
  const [deleteMeetModalVisible, setDeleteMeetModalVisible] = useState(false)
  const [deleteMeetPreviewLoading, setDeleteMeetPreviewLoading] = useState(false)
  const [deleteMeetSubmitting, setDeleteMeetSubmitting] = useState(false)
  const [deleteMeetPreview, setDeleteMeetPreview] =
    useState<IMeetRemovalPreview | null>(null)

  const history = useHistory()

  type DeclarationsStatus = "attending" | "notAttending" | "noResponse"

  const formatSwimmerNumberLocal = formatSwimmerNumber

  const refreshMeetAndDeclarations = useCallback(() => {
    setTriggerRefresh((prev) => prev + 1)
  }, [])

  const refreshMeetScheduleAndDeclarations = useCallback(() => {
    refreshData()
    refreshMeetAndDeclarations()
  }, [refreshData, refreshMeetAndDeclarations])

  const declarationsReportSorted = useMemo(
    () => {
      if (!declarationsReportSwimmers || declarationsReportSwimmers.length === 0) {
        return [] as IDeclarationsReportSwimmer[]
      }

      const statusWeight: Record<DeclarationsStatus, number> = {
        attending: 0,
        notAttending: 1,
        noResponse: 2,
      }

      return [...declarationsReportSwimmers].sort((a, b) => {
        const aStatus = deriveDeclarationStatus(a)
        const bStatus = deriveDeclarationStatus(b)

        if (statusWeight[aStatus] !== statusWeight[bStatus]) {
          return statusWeight[aStatus] - statusWeight[bStatus]
        }

        const aLast = (a.last_name || "").toLowerCase()
        const bLast = (b.last_name || "").toLowerCase()
        if (aLast !== bLast) {
          return aLast.localeCompare(bLast)
        }

        const aFirst = (a.first_name || "").toLowerCase()
        const bFirst = (b.first_name || "").toLowerCase()
        return aFirst.localeCompare(bFirst)
      })
    },
    [declarationsReportSwimmers],
  )

  // local aliases kept for backward-compatibility within this file

  useEffect(() => {
    setExportResultsVisible(false)
    setUpdateVirtualCourseVisible(false)
    setUpdateTraditionalCourseVisible(false)
    setUpdateMyMeetCourseVisible(false)
    setMeetLineupConfirmationVisible(false)
    setIsRenamingMeet(false)
    setRenameMeetSaving(false)
    setRenameMeetValue("")
    setShowRenameTrigger(false)
    setShowEntryLimitsTrigger(false)
    setIsEditingEntryLimits(false)
    setUpdateEntryLimitsSaving(false)
    setShowDateTimeTrigger(false)
    setShowMeetEntriesDeadlineTrigger(false)
    setIsEditingMeetDateTime(false)
    setIsMeetDatePickerOpen(false)
    setIsMeetTimePickerOpen(false)
    setUpdateMeetDateTimeSaving(false)
    setEditMeetDateValue(null)
    setEditMeetEndDateValue(null)
    setEditMeetTimeValue(null)
    setMeetCreatorClubId(null)
    setMeetEntriesDeadline(null)
    setAutoMarkHeatSheetReadyAfterDeadline(false)
    setIsEditingMeetEntriesDeadline(false)
    setEditMeetEntriesDeadlineValue(null)
    setEditAutoMarkHeatSheetReadyAfterDeadline(false)
    setUpdateMeetEntriesDeadlineSaving(false)
    setMeetEntriesDeadlineConfirmationVisible(false)
    setMeetEntriesDeadlineImpactPreview(null)
    setMeetEntriesDeadlineInfoVisible(false)
    setUpdatingHeatSheetReadyClubId(null)
  }, [meetId])

  useEffect(() => {
    if (visible) {
      if (initialActiveTab && initialActiveTab !== "declarations") {
        setActiveTab(initialActiveTab)
      } else if (!initialActiveTab) {
        setActiveTab("reportStudio")
      }
    }
  }, [initialActiveTab, visible])

  useEffect(() => {
    if (visible && initialActiveTab === "declarations" && supportsDeclarations) {
      setActiveTab("declarations")
    }
  }, [initialActiveTab, supportsDeclarations, visible])

  useEffect(() => {
    const clubSeason = meetClubSeasons.find(
      (mCS) => mCS.club.clubId === impersonateClub?.clubId,
    )
    setThisClubSeason(clubSeason || null)
  }, [impersonateClub?.clubId, meetClubSeasons])

  const getMeetInformation = useCallback(
    (meetId: number, clubId: number | undefined): Promise<{
      meet: IMeet
      clubSeasons: IClubSeasonInformation[]
      meetPdfStatus: IMeetPdfStatus
    }> => {
      return swimminglyApi
        .get(gen(`/api/getMeetInformation/${meetId}/${clubId}`))
        .then(
          ({
            status,
            meet,
            clubSeasons,
            meetPdfStatus,
          }: {
            status: string
            meet: IMeet
            clubSeasons: IClubSeasonInformation[]
            meetPdfStatus: IMeetPdfStatus
          }) => {
            if (status === "success") {
              return { meet, clubSeasons, meetPdfStatus }
            }
            throw new Error("Unsuccessful api request")
          },
        )
        .catch((err) => {
          console.error(err)
          throw new Error("Something went wrong in the api call")
        })
    },
    [],
  )

  const cloneThisMeet = useCallback(() => {
    setLoading(true)
    fetch(gen("/api/cloneMeet"), {
      method: "post",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "same-origin",
      body: JSON.stringify({ meetId: meetId }),
    })
      .then((result) => result.json())
      .then(({ status }) => {
        if (!status || status !== "success") {
          message.error("Uh oh, something went wrong")
        } else {
          message.success("Copied meet successfully!")
          const clubSeason = meetClubSeasons.find(
            (mCS) => mCS.club.clubId === impersonateClub?.clubId,
          )
          if (clubSeason?.clubSeasonId) {
            updateSeasonMeetArrayListener.emit(
              `updateSeasonMeets${clubSeason.clubSeasonId}`,
            )
          }
        }
      })
      .finally(() => {
        setLoading(false)
        setVisible(false)
      })
  }, [meetId, setLoading, setVisible, meetClubSeasons, impersonateClub?.clubId])

  useEffect(() => {
    if (visible && meetId !== -1) {
      setLoading(true)
      setMeetInfoLoading(true)
      setThisClubSeason(null)
      getMeetInformation(meetId, impersonateClub?.clubId)
        .then(({ meet, clubSeasons, meetPdfStatus }) => {
          const normalizedMeetStatus =
            meet.meetstatus === null || meet.meetstatus === undefined
              ? null
              : Number.isInteger(meet.meetstatus)
                ? (meet.meetstatus as 0 | 1 | 2 | 3)
                : (() => {
                    const parsedStatus = parseInt(String(meet.meetstatus), 10)
                    return Number.isInteger(parsedStatus)
                      ? (parsedStatus as 0 | 1 | 2 | 3)
                      : null
                  })()
          const normalizedIndividualLimit =
            typeof meet.individualLimit === "number"
              ? meet.individualLimit
              : meet.individualLimit === null || meet.individualLimit === undefined
                ? null
                : Number.isInteger(parseInt(String(meet.individualLimit), 10))
                  ? parseInt(String(meet.individualLimit), 10)
                  : null
          const normalizedRelayLimit =
            typeof meet.relayLimit === "number"
              ? meet.relayLimit
              : meet.relayLimit === null || meet.relayLimit === undefined
                ? null
                : Number.isInteger(parseInt(String(meet.relayLimit), 10))
                  ? parseInt(String(meet.relayLimit), 10)
                  : null
          const normalizedTotalLimit =
            typeof meet.totalLimit === "number"
              ? meet.totalLimit
              : meet.totalLimit === null || meet.totalLimit === undefined
                ? null
                : Number.isInteger(parseInt(String(meet.totalLimit), 10))
                  ? parseInt(String(meet.totalLimit), 10)
                  : null

          setMeetName(meet.meetName)
          setRenameMeetValue(meet.meetName)
          setIsVirtualMeet(Boolean(meet.isVirtualMeet))
          setMeetStatus(normalizedMeetStatus)
          setMeetResultSource(meet.resultSource ?? null)
          setMeetCreatorClubId(meet.creatorClubId ?? null)
          setMeetEntriesDeadline(meet.meetEntriesDeadline ?? null)
          setAutoMarkHeatSheetReadyAfterDeadline(
            Boolean(meet.autoMarkHeatSheetReadyAfterDeadline),
          )
          setMeetStartDate(dayjs(meet.startDate))
          setMeetEndDate(dayjs(meet.endDate))
          setMeetTime(meet.meetTime ?? null)
          setMeetClubId(meet.meetClubId ?? null)
          setVirtualMeetOrganizerClubId(
            meet.organizerClubId ?? meet.creatorClubId ?? null,
          )
          setHomeClubId(meet.is_home || -1)
          setVirtualMeetId(meet.virtualMeetId || null)
          setVirtualMeetHasResults(meet.hasVirtualResults || false)
          setMeetLocation(meet.location)
          setIndividualEntryLimit(normalizedIndividualLimit)
          setRelayEntryLimit(normalizedRelayLimit)
          setTotalEntryLimit(normalizedTotalLimit)
          setEditHasIndividualEntryLimit(normalizedIndividualLimit !== null)
          setEditHasRelayEntryLimit(normalizedRelayLimit !== null)
          setEditHasTotalEntryLimit(normalizedTotalLimit !== null)
          setEditIndividualEntryLimit(normalizedIndividualLimit)
          setEditRelayEntryLimit(normalizedRelayLimit)
          setEditTotalEntryLimit(normalizedTotalLimit)
          const numericUnitOfMeasure =
            typeof meet.unit_of_measure === "number"
              ? meet.unit_of_measure
              : meet.unit_of_measure != null
                ? Number(meet.unit_of_measure)
                : null

          setUnitOfMeasure(numericUnitOfMeasure)

          setCourse(
            numericUnitOfMeasure === 0
              ? "SCY"
              : numericUnitOfMeasure === 1
                ? "SCM"
                : "LCM",
          )
          if (meet.virtualCourse === undefined || meet.virtualCourse === null) {
            setVirtualCourse(null)
          } else {
            setVirtualCourse(
              meet.virtualCourse === 0
                ? "SCY"
                : meet.virtualCourse === 1
                  ? "SCM"
                  : "LCM",
            )
          }
          setMeetClubSeasons(clubSeasons)
          setThisClubSeason(
            clubSeasons.find(
              (mCS) => mCS.club.clubId === impersonateClub?.clubId,
            ) || null,
          )
          setNumClubsInMeet(clubSeasons.length)
          setMeetPdfStatus(meetPdfStatus)
        })
        .finally(() => {
          setLoading(false)
          setMeetInfoLoading(false)
        })
    } else if (!visible) {
      setMeetInfoLoading(false)
    }
  }, [
    visible,
    meetId,
    getMeetInformation,
    triggerRefresh,
    impersonateClub?.clubId,
  ])

  useEffect(() => {
    let mounted = true
    if (meetId !== -1 && visible) {
      swimminglyApi
        .post(gen("/api/getCurrentMeetPool"))
        .body({ meetId })
        .then((data) => {
          if (!mounted) return
          if (data && data.status === "success" && data.meetPool) {
            setMeetPool(data.meetPool as IMeetPool)
          }
        })
        .catch(() => {
          /* ignore pool errors in overview */
        })

      swimminglyApi
        .get(gen(`/api/getMeetEventOrderName/${meetId}`))
        .then(
          (data: {
            status: string
            eventOrderName?: string
          }) => {
            if (!mounted) return
            if (data.status === "success") {
              setEventOrderName(data.eventOrderName || null)
            }
          },
        )
        .catch(() => {
          /* ignore event order errors in overview */
        })

      swimminglyApi
        .get(gen(`/api/getMeetSessionContext/${meetId}`))
        .then((data: any) => {
          if (!mounted) return
          if (data.status === "success") {
            const ctx = data.sessionContext ?? null
            setMeetSessionContext(ctx)
            if (ctx?.multiSessionMeetId) {
              swimminglyApi
                .get(gen(`/api/getChampionshipDivisions/${ctx.multiSessionMeetId}`))
                .then((divData: any) => {
                  if (!mounted) return
                  if (divData.status === "success" && Array.isArray(divData.divisions)) {
                    setMeetDivisions(divData.divisions)
                  }
                })
                .catch(() => { /* no divisions */ })
            } else {
              setMeetDivisions([])
            }
          }
        })
        .catch(() => {
          /* not a multi-session meet or endpoint unavailable */
        })
    } else if (!visible) {
      setMeetSessionContext(null)
      setMeetDivisions([])
    }
    return () => {
      mounted = false
    }
  }, [meetId, visible, triggerRefresh])

  useEffect(() => {
    let mounted = true

    if (
      visible &&
      meetId !== -1 &&
      impersonateClub?.clubId &&
      supportsDeclarations
    ) {
      setDeclarationsReportLoading(true)
      swimminglyApi
        .get(
          gen(
            `/api/getClubSwimmersForMeetDeclarations?meetId=${meetId}&clubId=${impersonateClub.clubId}`,
          ),
        )
        .then(
          ({
            status,
            swimmers,
          }: {
            status: string
            swimmers: IDeclarationsReportSwimmer[]
          }) => {
            if (!mounted) return
            if (status !== "success") {
              message.error("Problem loading declarations report")
              setDeclarationsReportSwimmers([])
            } else {
              setDeclarationsReportSwimmers(swimmers || [])
            }
          },
        )
        .catch(() => {
          if (!mounted) return
          message.error("Problem loading declarations report")
          setDeclarationsReportSwimmers([])
        })
        .finally(() => {
          if (mounted) setDeclarationsReportLoading(false)
        })
    } else {
      setDeclarationsReportSwimmers([])
      setDeclarationsReportLoading(false)
    }

    return () => {
      mounted = false
    }
  }, [visible, meetId, impersonateClub?.clubId, triggerRefresh, supportsDeclarations])

  useEffect(() => {
    let mounted = true

    if (visible && meetId !== -1 && impersonateClub?.clubId) {
      swimminglyApi
        .get(
          gen(
            `/api/getMeetDeclarationsCloseDate/meetId=${meetId}/clubId=${impersonateClub.clubId}`,
          ),
        )
        .then((data: {
          meetDeclarationsCloseDate?: string | null
          parentEntries?: boolean
          parentAllowIndividualEntries?: boolean
          parentAllowRelayAvailability?: boolean
          parentAllowComments?: boolean
          supportsDeclarations?: boolean
        }) => {
          if (!mounted) return
          const closeDate = data.meetDeclarationsCloseDate || null
          const parentEntriesFromApi = Boolean(data.parentEntries)
          const supportsDeclarationsFromApi = data.supportsDeclarations !== false

          setMeetDeclarationsCloseDate(closeDate)
          setParentEntries(parentEntriesFromApi)
          setSupportsDeclarations(supportsDeclarationsFromApi)

          // For brand-new declarations (no deadline set and parents not
          // yet allowed to respond), default to attendance + comments ON
          // while keeping individual entries and relay availability OFF.
          if (!closeDate && !parentEntriesFromApi) {
            setParentAllowIndividualEntries(false)
            setParentAllowRelayAvailability(false)
            setParentAllowComments(true)
          } else {
            // Otherwise, respect whatever is stored for this meet/club,
            // defaulting to enabled when the flags are absent.
            setParentAllowIndividualEntries(
              data.parentAllowIndividualEntries !== false,
            )
            setParentAllowRelayAvailability(
              data.parentAllowRelayAvailability !== false,
            )
            setParentAllowComments(data.parentAllowComments !== false)
          }
        })
        .catch(() => {
          if (!mounted) return
          setMeetDeclarationsCloseDate(null)
          setParentEntries(false)
          setSupportsDeclarations(false)
          // On errors, fall back to the same defaults used for
          // unopened declarations.
          setParentAllowIndividualEntries(false)
          setParentAllowRelayAvailability(false)
          setParentAllowComments(true)
        })
    } else if (!visible) {
      setMeetDeclarationsCloseDate(null)
      setParentEntries(false)
      setSupportsDeclarations(false)
      // When closing the modal, reset to first-open defaults; the
      // next time it opens, the API response will re-sync as needed.
      setParentAllowIndividualEntries(false)
      setParentAllowRelayAvailability(false)
      setParentAllowComments(true)
    }

    return () => {
      mounted = false
    }
  }, [visible, meetId, impersonateClub?.clubId, triggerRefresh])

  useEffect(() => {
    let mounted = true

    if (visible && virtualMeetId !== null) {
      swimminglyApi
        .post(gen("/api/getScoringConfig"))
        .body({ virtualMeetId })
        .then((data: { status: string; scoringConfig?: any[] }) => {
          if (!mounted) return
          if (data.status !== "success" || !Array.isArray(data.scoringConfig)) {
            setIndividualScoringSummary(null)
            setRelayScoringSummary(null)
            return
          }

          const individualPoints = data.scoringConfig
            .filter((row) => row.isRelay === 0)
            .sort((a, b) => a.place - b.place)
            .map((row) => row.points)

          const relayPoints = data.scoringConfig
            .filter((row) => row.isRelay === 1)
            .sort((a, b) => a.place - b.place)
            .map((row) => row.points)

          const formatPoints = (arr: number[]) => (arr.length ? arr.join("‑") : null)

          setIndividualScoringSummary(formatPoints(individualPoints))
          setRelayScoringSummary(formatPoints(relayPoints))
        })
        .catch(() => {
          if (!mounted) return
          setIndividualScoringSummary(null)
          setRelayScoringSummary(null)
        })
    } else {
      setIndividualScoringSummary(null)
      setRelayScoringSummary(null)
    }

    return () => {
      mounted = false
    }
  }, [visible, virtualMeetId])

  const hostClub =
    meetClubSeasons.find((mCS) => mCS.club.clubId === homeClubId)?.club ?? null

  const dateLabel = isVirtualMeet
    ? `${meetStartDate.format("MMM D, YYYY")} – ${meetEndDate.format("MMM D, YYYY")}`
    : meetStartDate.format("MMM D, YYYY")

  const courseLabel = virtualCourse || course

  const formattedMeetTime = (() => {
    if (!meetTime) return null
    const trimmed = meetTime.trim()
    const match = trimmed.match(/^(\d{1,2}:\d{2})\s*(am|pm)$/i)
    if (!match) return trimmed
    const [, time, meridiem] = match
    return `${time} ${meridiem.toUpperCase()}`
  })()

  const parseMeetTimeInput = useCallback((value: string | null | undefined) => {
    if (!value) return null

    const trimmed = value.trim()
    const meridiemMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(am|pm)$/i)
    if (meridiemMatch) {
      let hours = parseInt(meridiemMatch[1], 10)
      const minutes = parseInt(meridiemMatch[2], 10)
      const meridiem = meridiemMatch[3].toLowerCase()

      if (meridiem === "pm" && hours < 12) {
        hours += 12
      }
      if (meridiem === "am" && hours === 12) {
        hours = 0
      }

      return dayjs().hour(hours).minute(minutes).second(0).millisecond(0)
    }

    const twentyFourHourMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
    if (twentyFourHourMatch) {
      const hours = parseInt(twentyFourHourMatch[1], 10)
      const minutes = parseInt(twentyFourHourMatch[2], 10)
      return dayjs().hour(hours).minute(minutes).second(0).millisecond(0)
    }

    return null
  }, [])

  const scoringSummaryText =
    isVirtualMeet && (individualScoringSummary || relayScoringSummary)
      ? [
          individualScoringSummary && `Individual ${individualScoringSummary}`,
          relayScoringSummary && `Relay ${relayScoringSummary}`,
        ]
          .filter(Boolean)
          .join("; ")
      : null

  const allowedMeetDateWindow = useMemo(() => {
    const seasonBounds = meetClubSeasons
      .map((clubSeason) => {
        const seasonStart = clubSeason.startDate || clubSeason.season?.startDate
        const seasonEnd = clubSeason.endDate || clubSeason.season?.endDate

        if (!seasonStart || !seasonEnd) {
          return null
        }

        const start = dayjs(seasonStart)
        const end = dayjs(seasonEnd)

        if (!start.isValid() || !end.isValid()) {
          return null
        }

        return { start, end }
      })
      .filter(
        (
          value,
        ): value is {
          start: Dayjs
          end: Dayjs
        } => Boolean(value),
      )

    if (seasonBounds.length === 0) {
      return null
    }

    const start = seasonBounds.reduce(
      (latest, current) =>
        current.start.isAfter(latest) ? current.start : latest,
      seasonBounds[0].start,
    )
    const end = seasonBounds.reduce(
      (earliest, current) =>
        current.end.isBefore(earliest) ? current.end : earliest,
      seasonBounds[0].end,
    )

    return { start, end }
  }, [meetClubSeasons])

  const isDateOutsideAllowedMeetWindow = useCallback(
    (value: Dayjs | null | undefined) => {
      if (!value || !allowedMeetDateWindow) {
        return false
      }

      if (allowedMeetDateWindow.end.diff(value, "day") < 0) {
        return true
      }

      if (allowedMeetDateWindow.start.diff(value, "day") > 0) {
        return true
      }

      return false
    },
    [allowedMeetDateWindow],
  )

  const meetStartDateTime =
    meetStartDate && meetStartDate.isValid()
      ? (() => {
          const parsedMeetTime = parseMeetTimeInput(meetTime)

          if (parsedMeetTime && parsedMeetTime.isValid()) {
            return meetStartDate
              .clone()
              .hour(parsedMeetTime.hour())
              .minute(parsedMeetTime.minute())
              .second(0)
              .millisecond(0)
          }

          // When no explicit time is set, treat the meet as starting at
          // the end of the day so declarations can run through that date.
          return meetStartDate.clone().endOf("day")
        })()
      : null

  const meetEntriesDeadlineFloor = dayjs().startOf("day")

  const declarationsPhase: "before" | "open" | "closed" = (() => {
    // If there's no deadline set and parents aren't allowed to respond,
    // treat this as "before" (not yet opened).
    if (!meetDeclarationsCloseDate && !parentEntries) {
      return "before"
    }

    const hasDeadline = Boolean(meetDeclarationsCloseDate)
    const close = hasDeadline ? dayjs(meetDeclarationsCloseDate) : null

    // When parents can submit entries, phase is driven by the deadline.
    if (parentEntries) {
      if (!close) return "before"
      return dayjs().isBefore(close) ? "open" : "closed"
    }

    // Parents have been explicitly closed off, so treat as closed
    // regardless of whether the deadline is in the future or past.
    return "closed"
  })()

  const thisClubStatus = thisClubSeason?.entryStatusInMeet ?? null
  // True when the meet's season endDate is strictly before today (day granularity)
  const seasonEnded = Boolean(
    thisClubSeason?.endDate && dayjs(thisClubSeason.endDate).isBefore(dayjs(), "day"),
  )
  const isSwimminglyAdmin = user?.role === 2
  const isLeagueAdmin = user?.role === 3

  const canCloneMeet =
    isSwimminglyAdmin &&
    meetName !== "2021 All American Swim Summer National Championships"

  const nameEditability = getMeetDetailEditability({
    field: "name",
    meetStatus,
    meetClubSeasons,
  })

  const dateTimeEditability = getMeetDetailEditability({
    field: "dateTime",
    meetStatus,
    meetClubSeasons,
  })

  const participatingClubsEditability = getMeetDetailEditability({
    field: "participatingClubs",
    meetStatus,
    meetClubSeasons,
  })

  const meetEntriesDeadlineEditability = getMeetDetailEditability({
    field: "meetEntriesDeadline",
    meetStatus,
    meetClubSeasons,
  })

  const canRenameMeet =
    isSwimminglyAdmin ||
    (nameEditability.canEdit &&
      meetName !== "2021 All American Swim Summer National Championships")

  const canEditMeetDateTime =
    isSwimminglyAdmin ||
    (dateTimeEditability.canEdit &&
      meetName !== "2021 All American Swim Summer National Championships")

  const canEditParticipatingClubs = participatingClubsEditability.canEdit
  const currentClubId = (() => {
    const parsedClubId = parseInt(
      `${impersonateClub?.clubId ?? user?.clubId ?? ""}`,
      10,
    )

    return Number.isInteger(parsedClubId) && parsedClubId > 0
      ? parsedClubId
      : null
  })()
  const impersonatedClubId = impersonateClub?.clubId ?? null
  const declarationsMeetStatus =
    impersonatedClubId !== null
      ? meetClubSeasons.find(
          (clubSeason) => clubSeason.club.clubId === impersonatedClubId,
        )?.entryStatusInMeet ?? meetStatus
      : meetStatus
  const meetEntriesDeadlineValue = useMemo(
    () => parseIsoToLocal(meetEntriesDeadline),
    [meetEntriesDeadline],
  )
  const declarationsDeadlineUpperIso = useMemo(() => {
    const deadlineCandidates = [meetStartDateTime, meetEntriesDeadlineValue].filter(
      (value): value is Dayjs => Boolean(value && value.isValid()),
    )

    if (deadlineCandidates.length === 0) {
      return null
    }

    return deadlineCandidates.reduce((earliest, current) =>
      current.isBefore(earliest) ? current : earliest,
    ).toISOString()
  }, [meetEntriesDeadlineValue, meetStartDateTime])
  const hasMeetEntriesDeadlinePassed = Boolean(
    meetEntriesDeadlineValue && !meetEntriesDeadlineValue.isAfter(dayjs()),
  )
  const hasSelectableMeetEntriesDeadlineDate = Boolean(
    meetStartDate?.isValid() &&
      !meetStartDate.endOf("day").isBefore(meetEntriesDeadlineFloor),
  )
  const canEditMeetEntriesDeadline =
    !isVirtualMeet &&
    meetEntriesDeadlineEditability.canEdit &&
    (isSwimminglyAdmin ||
      isLeagueAdmin ||
      (currentClubId !== null &&
        (meetCreatorClubId === currentClubId ||
          (meetCreatorClubId === null && homeClubId === currentClubId))))
  const canOpenMeetEntriesDeadlineEditor =
    canEditMeetEntriesDeadline && hasSelectableMeetEntriesDeadlineDate
  const canManageHeatSheetDeadlineOverrides =
    canEditMeetEntriesDeadline &&
    hasMeetEntriesDeadlinePassed &&
    (meetStatus === null || meetStatus === 0)
  const actingClubIdForDeadline = impersonateClub?.clubId || currentClubId || null
  const trackedClubCount = meetClubSeasons.length
  const heatSheetReadyCount = meetClubSeasons.filter(
    (clubSeason) => Boolean(clubSeason.confirmLineup),
  ).length
  const pendingHeatSheetReadyCount = meetClubSeasons.filter(
    (clubSeason) => !clubSeason.confirmLineup,
  ).length

  const canEditHostClub = canEditParticipatingClubs && !isVirtualMeet
  const canEditMeetPool =
    (meetStatus === null || meetStatus === 0) &&
    (isSwimminglyAdmin ||
      (isVirtualMeet
        ? currentClubId !== null && meetClubId === currentClubId
        : participatingClubsEditability.canEdit))
  const canEditVirtualMeetCourses =
    isSwimminglyAdmin || meetStatus === null || meetStatus === 0

  const canEditMyMeetCourse =
    isVirtualMeet &&
    canEditVirtualMeetCourses &&
    (isSwimminglyAdmin ||
      (currentClubId !== null && meetClubId === currentClubId)) &&
    (isSwimminglyAdmin ||
      meetName !== "2021 All American Swim Summer National Championships")

  const canUpdateVirtualCourse =
    isVirtualMeet &&
    canEditVirtualMeetCourses &&
    virtualMeetId !== null &&
    (isSwimminglyAdmin ||
      (currentClubId !== null && virtualMeetOrganizerClubId === currentClubId)) &&
    (isSwimminglyAdmin ||
      meetName !== "2021 All American Swim Summer National Championships")

  const canEditEntryLimits = !isVirtualMeet && (thisClubStatus === null || thisClubStatus === 0)

  const canImportEntries = thisClubStatus !== 2
  const showDeleteMeetAction = !isVirtualMeet

  const openMeetEntriesDeadlineEditor = useCallback(() => {
    if (!canOpenMeetEntriesDeadlineEditor) {
      return
    }

    setEditMeetEntriesDeadlineValue(
      meetEntriesDeadlineValue ? dayjs(meetEntriesDeadlineValue.toDate()) : null,
    )
    setEditAutoMarkHeatSheetReadyAfterDeadline(
      autoMarkHeatSheetReadyAfterDeadline,
    )
    setShowMeetEntriesDeadlineTrigger(true)
    setIsEditingMeetEntriesDeadline(true)
  }, [
    autoMarkHeatSheetReadyAfterDeadline,
    canOpenMeetEntriesDeadlineEditor,
    meetEntriesDeadlineValue,
  ])

  const cancelMeetEntriesDeadlineEdit = useCallback(() => {
    const nextDeadlineValue = meetEntriesDeadlineValue
      ? dayjs(meetEntriesDeadlineValue.toDate())
      : null
    const currentEditDeadlineIso = editMeetEntriesDeadlineValue
      ? editMeetEntriesDeadlineValue.toISOString()
      : null
    const nextEditDeadlineIso = nextDeadlineValue
      ? nextDeadlineValue.toISOString()
      : null

    if (currentEditDeadlineIso !== nextEditDeadlineIso) {
      setEditMeetEntriesDeadlineValue(nextDeadlineValue)
    }

    if (
      editAutoMarkHeatSheetReadyAfterDeadline !==
      autoMarkHeatSheetReadyAfterDeadline
    ) {
      setEditAutoMarkHeatSheetReadyAfterDeadline(
        autoMarkHeatSheetReadyAfterDeadline,
      )
    }

    if (showMeetEntriesDeadlineTrigger) {
      setShowMeetEntriesDeadlineTrigger(false)
    }

    if (isEditingMeetEntriesDeadline) {
      setIsEditingMeetEntriesDeadline(false)
    }
  }, [
    autoMarkHeatSheetReadyAfterDeadline,
    editAutoMarkHeatSheetReadyAfterDeadline,
    editMeetEntriesDeadlineValue,
    isEditingMeetEntriesDeadline,
    meetEntriesDeadlineValue,
    showMeetEntriesDeadlineTrigger,
  ])

  useEffect(() => {
    if (!visible || isVirtualMeet) {
      return
    }

    if (!canEditMeetEntriesDeadline) {
      cancelMeetEntriesDeadlineEdit()
      return
    }

    if (!isEditingMeetEntriesDeadline) {
      const nextDeadlineValue = meetEntriesDeadlineValue
        ? dayjs(meetEntriesDeadlineValue.toDate())
        : null
      const currentEditDeadlineIso = editMeetEntriesDeadlineValue
        ? editMeetEntriesDeadlineValue.toISOString()
        : null
      const nextEditDeadlineIso = nextDeadlineValue
        ? nextDeadlineValue.toISOString()
        : null

      if (currentEditDeadlineIso !== nextEditDeadlineIso) {
        setEditMeetEntriesDeadlineValue(nextDeadlineValue)
      }

      if (
        editAutoMarkHeatSheetReadyAfterDeadline !==
        autoMarkHeatSheetReadyAfterDeadline
      ) {
        setEditAutoMarkHeatSheetReadyAfterDeadline(
          autoMarkHeatSheetReadyAfterDeadline,
        )
      }
    }
  }, [
    editAutoMarkHeatSheetReadyAfterDeadline,
    editMeetEntriesDeadlineValue,
    autoMarkHeatSheetReadyAfterDeadline,
    canEditMeetEntriesDeadline,
    cancelMeetEntriesDeadlineEdit,
    isEditingMeetEntriesDeadline,
    isVirtualMeet,
    meetEntriesDeadlineValue,
    visible,
  ])

  const submitMeetEntriesDeadlineUpdate = useCallback((confirmDeadlineClamp = false) => {
    if (editMeetEntriesDeadlineValue) {
      if (editMeetEntriesDeadlineValue.isBefore(meetEntriesDeadlineFloor)) {
        message.error("Deadline cannot be in the past")
        return
      }

      if (meetStartDateTime?.isValid() && editMeetEntriesDeadlineValue.isAfter(meetStartDateTime)) {
        message.error("Meet entries deadline cannot be after the meet start time")
        return
      }
    }

    const nextDeadlineIso = editMeetEntriesDeadlineValue
      ? toIsoStringUTC(editMeetEntriesDeadlineValue)
      : null
    const nextAutoMark = nextDeadlineIso
      ? editAutoMarkHeatSheetReadyAfterDeadline
      : false
    const currentDeadlineIso = meetEntriesDeadlineValue
      ? toIsoStringUTC(meetEntriesDeadlineValue)
      : null

    if (
      nextDeadlineIso === currentDeadlineIso &&
      nextAutoMark === autoMarkHeatSheetReadyAfterDeadline
    ) {
      setShowMeetEntriesDeadlineTrigger(false)
      setIsEditingMeetEntriesDeadline(false)
      return
    }

    setUpdateMeetEntriesDeadlineSaving(true)
    swimminglyApi
      .post(gen("/api/updateMeetEntriesDeadline"))
      .body({
        meetId,
        meetEntriesDeadline: nextDeadlineIso,
        meetStartDateTimeUtc: meetStartDateTime
          ? toIsoStringUTC(meetStartDateTime)
          : null,
        autoMarkHeatSheetReadyAfterDeadline: nextAutoMark,
        confirmDeadlineClamp,
        actingClubId: actingClubIdForDeadline,
        ...getActingUserRequestFields(actingUser),
      })
      .then((data: {
        status?: string
        message?: string
        confirmationRequired?: boolean
        deadlineImpactPreview?: IMeetEntriesDeadlineImpactPreview
        statusCode?: number
      }) => {
        if (
          data?.status === "confirmation_required" ||
          (data?.confirmationRequired && data?.statusCode === 409)
        ) {
          setMeetEntriesDeadlineImpactPreview(data.deadlineImpactPreview || null)
          setMeetEntriesDeadlineConfirmationVisible(true)
          return
        }

        if (data?.status !== "success") {
          throw new Error(
            data?.message || "Problem updating meet entries deadline.",
          )
        }

        setMeetEntriesDeadline(nextDeadlineIso)
        setAutoMarkHeatSheetReadyAfterDeadline(nextAutoMark)
  setMeetEntriesDeadlineImpactPreview(null)
  setMeetEntriesDeadlineConfirmationVisible(false)
        setShowMeetEntriesDeadlineTrigger(false)
        setIsEditingMeetEntriesDeadline(false)
        refreshData()
        refreshMeetAndDeclarations()
        message.success(
          data.message || "Meet entries deadline updated successfully.",
        )
      })
      .catch((err) => {
        console.error(err)
        message.error(
          err?.message || "Problem updating meet entries deadline.",
        )
      })
      .finally(() => {
        setUpdateMeetEntriesDeadlineSaving(false)
      })
  }, [
    actingUser,
    actingClubIdForDeadline,
    autoMarkHeatSheetReadyAfterDeadline,
    editAutoMarkHeatSheetReadyAfterDeadline,
    editMeetEntriesDeadlineValue,
    meetEntriesDeadlineFloor,
    meetEntriesDeadlineValue,
    meetStartDateTime,
    meetId,
    refreshData,
    refreshMeetAndDeclarations,
  ])

  const handleUpdateMeetEntriesDeadline = useCallback(() => {
    submitMeetEntriesDeadlineUpdate(false)
  }, [submitMeetEntriesDeadlineUpdate])

  const confirmMeetEntriesDeadlineUpdate = useCallback(() => {
    submitMeetEntriesDeadlineUpdate(true)
  }, [submitMeetEntriesDeadlineUpdate])

  const formatMeetEntriesDeadlineImpactDate = useCallback((value: string | null) => {
    if (!value) {
      return "Not configured"
    }

    const parsedValue = dayjs(value)
    if (!parsedValue.isValid()) {
      return value
    }

    return parsedValue.format("MMM D, YYYY h:mm a")
  }, [])

  const handleMarkMeetClubHeatSheetReadyAfterDeadline = useCallback(
    (targetClubId: number) => {
      setUpdatingHeatSheetReadyClubId(targetClubId)
      swimminglyApi
        .post(gen("/api/markMeetClubHeatSheetReadyAfterDeadline"))
        .body({
          meetId,
          targetClubId,
          actingClubId: actingClubIdForDeadline,
        })
        .then((data: { status?: string; message?: string }) => {
          if (data?.status !== "success") {
            throw new Error("Problem marking club heat sheet ready")
          }

          refreshData()
          refreshMeetAndDeclarations()
          message.success(
            data.message || "Club marked entries completed and heat sheet ready.",
          )
        })
        .catch((err) => {
          console.error(err)
          message.error("Problem marking club heat sheet ready.")
        })
        .finally(() => {
          setUpdatingHeatSheetReadyClubId(null)
        })
    },
    [actingClubIdForDeadline, meetId, refreshData, refreshMeetAndDeclarations],
  )

  const openDeleteMeetModal = () => {
    setDeleteMeetModalVisible(true)
    setDeleteMeetPreviewLoading(true)
    setDeleteMeetPreview(null)

    swimminglyApi
      .get(gen(`/api/getMeetRemovalPreview/${meetId}`))
      .then((data: { status?: string } & IMeetRemovalPreview) => {
        if (data?.status === "success") {
          setDeleteMeetPreview(data)
          return
        }

        message.error("Problem loading meet removal details.")
        setDeleteMeetModalVisible(false)
      })
      .catch(() => {
        message.error("Problem loading meet removal details.")
        setDeleteMeetModalVisible(false)
      })
      .finally(() => setDeleteMeetPreviewLoading(false))
  }

  const handleDeleteMeet = () => {
    if (!deleteMeetPreview?.permissions.canDelete) {
      return
    }

    setDeleteMeetSubmitting(true)
    swimminglyApi
      .post(gen(`/api/softRemoveMeet`))
      .body({
        meetId,
        effectiveUserId: user?.userId || null,
        effectiveClubId: impersonateClub?.clubId || null,
      })
      .then(() => {
        refreshData()
        refreshMeetAndDeclarations()
        setDeleteMeetModalVisible(false)
        setVisible(false)
        message.success("Meet was removed.", 4)
      })
      .catch((error: { response?: { data?: { error?: string } } }) => {
        message.error(
          error?.response?.data?.error || "Problem removing this meet.",
        )
      })
      .finally(() => setDeleteMeetSubmitting(false))
  }

  const handleRenameMeet = () => {
    const nextMeetName = renameMeetValue.trim()

    if (nextMeetName.length < 2) {
      message.error("Meet name must be at least 2 characters.")
      return
    }

    if (nextMeetName === meetName) {
      setIsRenamingMeet(false)
      setShowRenameTrigger(false)
      return
    }

    setRenameMeetSaving(true)
    const isVirtualRename = virtualMeetId !== null
    swimminglyApi
      .post(gen("/api/updateMeetName"))
      .body(
        isVirtualRename
          ? {
              virtualMeetId,
              meetName: nextMeetName,
              ...getActingUserRequestFields(actingUser),
            }
          : {
              meetId,
              meetName: nextMeetName,
              ...getActingUserRequestFields(actingUser),
            },
      )
      .then(
        (data: {
          status?: string
          message?: string
          swimminglyFan?: { attempted?: boolean; ok?: boolean; message?: string }
        }) => {
        if (data?.status !== "success" && data?.status !== "warning") {
          throw new Error("Problem updating meet name")
        }

        setMeetName(nextMeetName)
        setRenameMeetValue(nextMeetName)
        setIsRenamingMeet(false)
        setShowRenameTrigger(false)
        refreshData()
        refreshMeetAndDeclarations()
        if (data?.status === "warning") {
          message.warning(
            data.message ||
              (isVirtualRename
                ? "Virtual swim meet name updated in Clubhouse, but one or more SwimminglyFan syncs failed."
                : "Meet name updated in Clubhouse, but SwimminglyFan sync failed."),
            5,
          )
        } else {
          message.success(
            data.message ||
              (isVirtualRename
                ? "Virtual swim meet name updated in Clubhouse and synced to SwimminglyFan."
                : "Meet name updated in Clubhouse and synced to SwimminglyFan."),
          )
        }
      })
      .catch((err) => {
        console.error(err)
        message.error("Problem updating meet name.")
      })
      .finally(() => {
        setRenameMeetSaving(false)
      })
  }

  const cancelRenameMeet = useCallback(() => {
    setRenameMeetValue(meetName)
    setIsRenamingMeet(false)
    setShowRenameTrigger(false)
  }, [meetName])

  const openEntryLimitsEditor = useCallback(() => {
    setEditHasIndividualEntryLimit(individualEntryLimit !== null)
    setEditHasRelayEntryLimit(relayEntryLimit !== null)
    setEditHasTotalEntryLimit(totalEntryLimit !== null)
    setEditIndividualEntryLimit(individualEntryLimit)
    setEditRelayEntryLimit(relayEntryLimit)
    setEditTotalEntryLimit(totalEntryLimit)
    setShowEntryLimitsTrigger(true)
    setIsEditingEntryLimits(true)
  }, [individualEntryLimit, relayEntryLimit, totalEntryLimit])

  const cancelEntryLimitsEdit = useCallback(() => {
    setEditHasIndividualEntryLimit(individualEntryLimit !== null)
    setEditHasRelayEntryLimit(relayEntryLimit !== null)
    setEditHasTotalEntryLimit(totalEntryLimit !== null)
    setEditIndividualEntryLimit(individualEntryLimit)
    setEditRelayEntryLimit(relayEntryLimit)
    setEditTotalEntryLimit(totalEntryLimit)
    setShowEntryLimitsTrigger(false)
    setIsEditingEntryLimits(false)
  }, [individualEntryLimit, relayEntryLimit, totalEntryLimit])

  const submitEntryLimitUpdate = useCallback(
    (confirmOverLimit = false) => {
      if (editHasIndividualEntryLimit && editIndividualEntryLimit === null) {
        message.error("Please enter an individual entry limit or mark it as No Limit.")
        return
      }

      if (editHasRelayEntryLimit && editRelayEntryLimit === null) {
        message.error("Please enter a relay entry limit or mark it as No Limit.")
        return
      }

      if (editHasTotalEntryLimit && editTotalEntryLimit === null) {
        message.error("Please enter a total entry limit or mark it as No Limit.")
        return
      }

      const nextIndividualLimit = editHasIndividualEntryLimit
        ? editIndividualEntryLimit
        : null
      const nextRelayLimit = editHasRelayEntryLimit ? editRelayEntryLimit : null
      const nextTotalLimit = editHasTotalEntryLimit ? editTotalEntryLimit : null

      if (
        nextIndividualLimit === individualEntryLimit &&
        nextRelayLimit === relayEntryLimit &&
        nextTotalLimit === totalEntryLimit
      ) {
        setShowEntryLimitsTrigger(false)
        setIsEditingEntryLimits(false)
        return
      }

      setUpdateEntryLimitsSaving(true)
      swimminglyApi
        .post(gen("/api/updateMeetEntryLimits"))
        .body({
          meetId,
          individualEntryLimit: nextIndividualLimit,
          relayEntryLimit: nextRelayLimit,
          totalEntryLimit: nextTotalLimit,
          confirmOverLimit,
          ...getActingUserRequestFields(actingUser),
        })
        .then((data: {
          status?: string
          code?: string
          message?: string
        }) => {
          if (
            data?.status === "warning" &&
            data?.code === "entry-limit-overage-warning" &&
            !confirmOverLimit
          ) {
            Modal.confirm({
              title: "Save stricter entry limits?",
              icon: <ExclamationCircleTwoTone twoToneColor="#faad14" />,
              content:
                data.message ||
                "Some swimmers already exceed the new entry limits. Existing entries will remain, but future entry edits will enforce the new limits.",
              okText: "Save limits",
              cancelText: "Cancel",
              onOk: () => submitEntryLimitUpdate(true),
            })
            return
          }

          if (data?.status !== "success") {
            throw new Error("Problem updating meet entry limits")
          }

          setIndividualEntryLimit(nextIndividualLimit)
          setRelayEntryLimit(nextRelayLimit)
          setTotalEntryLimit(nextTotalLimit)
          setShowEntryLimitsTrigger(false)
          setIsEditingEntryLimits(false)
          refreshData()
          refreshMeetAndDeclarations()
          message.success(data.message || "Meet entry limits updated successfully.")
        })
        .catch((err) => {
          console.error(err)
          message.error("Problem updating meet entry limits.")
        })
        .finally(() => {
          setUpdateEntryLimitsSaving(false)
        })
    },
    [
      actingUser,
      editHasIndividualEntryLimit,
      editHasRelayEntryLimit,
      editHasTotalEntryLimit,
      editIndividualEntryLimit,
      editRelayEntryLimit,
      editTotalEntryLimit,
      individualEntryLimit,
      meetId,
      refreshData,
      refreshMeetAndDeclarations,
      relayEntryLimit,
      totalEntryLimit,
    ],
  )

  const renderEntryLimitsOverviewContent = () => {
    const limitSummary = (
      <span>
        <span style={{ fontWeight: 400 }}>Entry limits: </span>
        {individualEntryLimit !== null ? (
          <span style={{ fontWeight: 600 }}>Individual {individualEntryLimit}</span>
        ) : null}
        {individualEntryLimit !== null && relayEntryLimit !== null ? (
          <span style={{ marginLeft: 8 }}>· </span>
        ) : null}
        {relayEntryLimit !== null ? (
          <span style={{ fontWeight: 600 }}>Relay {relayEntryLimit}</span>
        ) : null}
        {totalEntryLimit !== null ? (
          <span>
            <span style={{ marginLeft: 8 }}>· </span>
            <span style={{ fontWeight: 600 }}>Total {totalEntryLimit}</span>
          </span>
        ) : null}
        {individualEntryLimit === null && relayEntryLimit === null && totalEntryLimit === null ? (
          <span style={{ fontWeight: 600 }}>No limits</span>
        ) : null}
      </span>
    )

    if (!isEditingEntryLimits) {
      if (canEditEntryLimits && !seasonEnded) {
        return (
        <span
          ref={entryLimitsAreaRef}
          className={`meet-inline-edit-display${showEntryLimitsTrigger ? " meet-inline-edit-visible" : ""}`}
          onClick={() => {
            if (!showEntryLimitsTrigger) {
              setShowEntryLimitsTrigger(true)
            }
          }}
          onKeyDown={(event) => {
            if ((event.key === "Enter" || event.key === " ") && !showEntryLimitsTrigger) {
              event.preventDefault()
              setShowEntryLimitsTrigger(true)
            }
          }}
          role="button"
          tabIndex={0}
        >
          <span className="meet-inline-edit-display-row">
            {limitSummary}
            <Button
              type="link"
              className="meet-inline-edit-trigger"
              size="small"
              onClick={(event) => {
                event.stopPropagation()
                openEntryLimitsEditor()
              }}
              style={{ display: "inline-flex", alignItems: "center" }}
              aria-label="Edit entry limits"
            >
              <FontAwesomeIcon icon={faPenToSquare} />
            </Button>
          </span>
        </span>
        )
      }
      if (canEditEntryLimits && seasonEnded) {
        return (
          <span className="meet-inline-edit-display-row">
            {limitSummary}
            {seasonEndedEditIcon}
          </span>
        )
      }
      return limitSummary
    }

    return (
      <span style={{ display: "inline-flex", flexDirection: "column", gap: 10, width: "100%" }}>
        <span style={{ fontWeight: 400 }}>Entry limits:</span>
        <span style={{ display: "grid", gap: 8 }}>
          {[
            {
              key: "individual",
              label: "Individual",
              checked: editHasIndividualEntryLimit,
              value: editIndividualEntryLimit,
              setChecked: setEditHasIndividualEntryLimit,
              setValue: setEditIndividualEntryLimit,
            },
            {
              key: "relay",
              label: "Relay",
              checked: editHasRelayEntryLimit,
              value: editRelayEntryLimit,
              setChecked: setEditHasRelayEntryLimit,
              setValue: setEditRelayEntryLimit,
            },
            {
              key: "total",
              label: "Total",
              checked: editHasTotalEntryLimit,
              value: editTotalEntryLimit,
              setChecked: setEditHasTotalEntryLimit,
              setValue: setEditTotalEntryLimit,
            },
          ].map((limitField) => (
            <span
              key={limitField.key}
              style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
            >
              <span style={{ minWidth: 72, fontWeight: 600 }}>{limitField.label}</span>
              <Input
                type="number"
                min={0}
                step={1}
                style={{ width: 96 }}
                disabled={!limitField.checked || updateEntryLimitsSaving}
                value={limitField.value ?? ""}
                onChange={(event) => {
                  const nextValue = event.target.value
                  limitField.setValue(nextValue === "" ? null : parseInt(nextValue, 10))
                }}
              />
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <input
                  type="checkbox"
                  checked={!limitField.checked}
                  disabled={updateEntryLimitsSaving}
                  onChange={(event) => {
                    const hasLimit = !event.target.checked
                    limitField.setChecked(hasLimit)
                    if (!hasLimit) {
                      limitField.setValue(null)
                    }
                  }}
                />
                No Limit
              </label>
            </span>
          ))}
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Button
            type="text"
            size="small"
            aria-label="Save entry limits"
            style={{ color: "#389e0d" }}
            icon={<FontAwesomeIcon icon={faCircleCheck} />}
            loading={updateEntryLimitsSaving}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => submitEntryLimitUpdate(false)}
          />
          <Button
            type="text"
            size="small"
            aria-label="Cancel entry limits edit"
            style={{ color: "#cf1322" }}
            icon={<FontAwesomeIcon icon={faCircleXmark} />}
            onMouseDown={(e) => e.preventDefault()}
            onClick={cancelEntryLimitsEdit}
            disabled={updateEntryLimitsSaving}
          />
        </span>
      </span>
    )
  }

  const openMeetDateTimeEditor = useCallback(() => {
    setEditMeetDateValue(meetStartDate)
    setEditMeetEndDateValue(meetEndDate)
    setEditMeetTimeValue(parseMeetTimeInput(meetTime))
    setIsEditingMeetDateTime(true)
    setShowDateTimeTrigger(true)
    setIsMeetDatePickerOpen(false)
    setIsMeetTimePickerOpen(false)
  }, [meetEndDate, meetStartDate, meetTime, parseMeetTimeInput])

  const cancelMeetDateTimeEdit = useCallback(() => {
    setEditMeetDateValue(meetStartDate)
    setEditMeetEndDateValue(meetEndDate)
    setEditMeetTimeValue(parseMeetTimeInput(meetTime))
    setIsEditingMeetDateTime(false)
    setShowDateTimeTrigger(false)
    setIsMeetDatePickerOpen(false)
    setIsMeetTimePickerOpen(false)
  }, [meetEndDate, meetStartDate, meetTime, parseMeetTimeInput])

  const handleUpdateMeetDateTime = useCallback(() => {
    if (!editMeetDateValue) {
      message.error(isVirtualMeet ? "Please choose a meet start date." : "Please choose a meet date.")
      return
    }

    if (isVirtualMeet && !editMeetEndDateValue) {
      message.error("Please choose a meet end date.")
      return
    }

    if (!editMeetTimeValue) {
      message.error("Please choose a meet time.")
      return
    }

    const nextStartDate = editMeetDateValue.format("YYYY-MM-DD")
    const nextEndDate = (isVirtualMeet ? editMeetEndDateValue : editMeetDateValue)?.format("YYYY-MM-DD")
    const nextMeetTime = editMeetTimeValue.format("hh:mma")
    const currentStartDate = meetStartDate.format("YYYY-MM-DD")
    const currentEndDate = meetEndDate.format("YYYY-MM-DD")
    const currentMeetTime = meetTime || ""

    if (isVirtualMeet && nextEndDate && nextStartDate > nextEndDate) {
      message.error("Meet start date cannot be after the meet end date.")
      return
    }

    if (
      nextStartDate === currentStartDate &&
      (nextEndDate || nextStartDate) === currentEndDate &&
      nextMeetTime === currentMeetTime
    ) {
      setIsEditingMeetDateTime(false)
      setShowDateTimeTrigger(false)
      return
    }

    setUpdateMeetDateTimeSaving(true)
    swimminglyApi
      .post(gen("/api/updateMeetDateTime"))
      .body(
        isVirtualMeet
          ? {
              virtualMeetId,
              startDate: nextStartDate,
              endDate: nextEndDate,
              meetTime: nextMeetTime,
              ...getActingUserRequestFields(actingUser),
            }
          : {
              meetId,
              meetDate: nextStartDate,
              meetTime: nextMeetTime,
              ...getActingUserRequestFields(actingUser),
            },
      )
      .then(
        (data: {
          status?: string
          message?: string
        }) => {
          if (data?.status !== "success" && data?.status !== "warning") {
            throw new Error("Problem updating meet date/time")
          }

          const nextStart = dayjs(nextStartDate)
          const nextEnd = dayjs(nextEndDate || nextStartDate)
          setMeetStartDate(nextStart)
          setMeetEndDate(nextEnd)
          setMeetTime(nextMeetTime)
          setIsEditingMeetDateTime(false)
          setShowDateTimeTrigger(false)
          refreshData()
          refreshMeetAndDeclarations()

          if (data.status === "warning") {
            message.warning(
              data.message ||
                (isVirtualMeet
                  ? "Virtual swim meet date and time updated in Clubhouse, but one or more SwimminglyFan syncs failed."
                  : "Meet date and time updated in Clubhouse, but SwimminglyFan sync failed."),
              5,
            )
          } else {
            message.success(
              data.message ||
                (isVirtualMeet
                  ? "Virtual swim meet date and time updated in Clubhouse and synced to SwimminglyFan."
                  : "Meet date and time updated in Clubhouse and synced to SwimminglyFan."),
            )
          }
        },
      )
      .catch((err) => {
        console.error(err)
        message.error("Problem updating meet date and time.")
      })
      .finally(() => {
        setUpdateMeetDateTimeSaving(false)
      })
  }, [
    actingUser,
    editMeetDateValue,
    editMeetEndDateValue,
    editMeetTimeValue,
    isVirtualMeet,
    meetId,
    meetEndDate,
    meetStartDate,
    meetTime,
    refreshData,
    refreshMeetAndDeclarations,
    virtualMeetId,
  ])

  const renderRenameField = (className?: string) => (
    <span className={className ? `meet-name-editing ${className}` : "meet-name-editing"}>
      <span className="meet-name-edit-field">
        <Input
          className="meet-name-edit-input"
          autoFocus
          maxLength={120}
          placeholder="Enter meet name"
          value={renameMeetValue}
          onChange={(e) => setRenameMeetValue(e.target.value)}
          onPressEnter={handleRenameMeet}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault()
              cancelRenameMeet()
            }
          }}
          onBlur={() => {
            if (!renameMeetSaving) {
              cancelRenameMeet()
            }
          }}
          disabled={renameMeetSaving}
        />
        <span className="meet-name-edit-actions">
          <Button
            type="text"
            size="small"
            aria-label="Save meet name"
            style={{ color: "#389e0d" }}
            icon={<FontAwesomeIcon icon={faCircleCheck} />}
            loading={renameMeetSaving}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleRenameMeet}
          />
          <Button
            type="text"
            size="small"
            aria-label="Cancel meet name edit"
            style={{ color: "#cf1322" }}
            icon={<FontAwesomeIcon icon={faCircleXmark} />}
            onMouseDown={(e) => e.preventDefault()}
            onClick={cancelRenameMeet}
            disabled={renameMeetSaving}
          />
        </span>
      </span>
    </span>
  )

  useEffect(() => {
    if (!showRenameTrigger || isRenamingMeet) {
      return
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (
        meetNameDisplayRef.current &&
        event.target instanceof Node &&
        !meetNameDisplayRef.current.contains(event.target)
      ) {
        setShowRenameTrigger(false)
      }
    }

    document.addEventListener("pointerdown", handlePointerDown)

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown)
    }
  }, [showRenameTrigger, isRenamingMeet])

  useEffect(() => {
    if (!showEntryLimitsTrigger || isEditingEntryLimits) {
      return
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (
        entryLimitsAreaRef.current &&
        event.target instanceof Node &&
        !entryLimitsAreaRef.current.contains(event.target)
      ) {
        setShowEntryLimitsTrigger(false)
      }
    }

    document.addEventListener("pointerdown", handlePointerDown)
    return () => document.removeEventListener("pointerdown", handlePointerDown)
  }, [showEntryLimitsTrigger, isEditingEntryLimits])

  useEffect(() => {
    if (!showDateTimeTrigger || isEditingMeetDateTime) {
      return
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target

      if (
        target instanceof Element &&
        target.closest(".ant-picker-dropdown")
      ) {
        return
      }

      if (
        meetDateTimeAreaRef.current &&
        target instanceof Node &&
        !meetDateTimeAreaRef.current.contains(target)
      ) {
        setShowDateTimeTrigger(false)
      }
    }

    document.addEventListener("pointerdown", handlePointerDown)

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown)
    }
  }, [showDateTimeTrigger, isEditingMeetDateTime])

  useEffect(() => {
    if (!showMeetEntriesDeadlineTrigger || isEditingMeetEntriesDeadline) {
      return
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (
        meetEntriesDeadlineAreaRef.current &&
        event.target instanceof Node &&
        !meetEntriesDeadlineAreaRef.current.contains(event.target)
      ) {
        setShowMeetEntriesDeadlineTrigger(false)
      }
    }

    document.addEventListener("pointerdown", handlePointerDown)
    return () => document.removeEventListener("pointerdown", handlePointerDown)
  }, [showMeetEntriesDeadlineTrigger, isEditingMeetEntriesDeadline])

  useEffect(() => {
    if (!isEditingMeetDateTime) {
      return
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target

      if (
        target instanceof Element &&
        target.closest(".ant-picker-dropdown")
      ) {
        return
      }

      if (
        meetDateTimeAreaRef.current &&
        target instanceof Node &&
        !meetDateTimeAreaRef.current.contains(target)
      ) {
        cancelMeetDateTimeEdit()
      }
    }

    document.addEventListener("pointerdown", handlePointerDown)

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown)
    }
  }, [isEditingMeetDateTime, cancelMeetDateTimeEdit])

  useEffect(() => {
    if (!isEditingMeetDateTime || isMeetDatePickerOpen || isMeetTimePickerOpen) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault()
        cancelMeetDateTimeEdit()
        return
      }

      if (event.key === "Enter") {
        event.preventDefault()
        handleUpdateMeetDateTime()
      }
    }

    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [
    cancelMeetDateTimeEdit,
    handleUpdateMeetDateTime,
    isEditingMeetDateTime,
    isMeetDatePickerOpen,
    isMeetTimePickerOpen,
  ])

  // Renders a disabled lock icon with a tooltip when the season has ended,
  // replacing the normal pencil edit button.
  const seasonEndedEditIcon = (
    <Tooltip title="This season has ended. This action is no longer available.">
      <Button
        type="link"
        size="small"
        disabled
        style={{ paddingInline: 0, height: "auto", cursor: "not-allowed" }}
        aria-label="Editing disabled — season has ended"
      >
        <FontAwesomeIcon icon={faLock} />
      </Button>
    </Tooltip>
  )

  const renderMeetDateTimeOverview = () => {
    const dateLabelText = isVirtualMeet ? "Meet Window: " : "Meet Date: "

    if (isEditingMeetDateTime) {
      return (
        <div
          ref={meetDateTimeAreaRef}
          className="meet-inline-editing"
        >
          <div className="meet-inline-editing-row">
            <span style={{ marginRight: 6 }}>
              <FontAwesomeIcon icon={faCalendar} />
            </span>
            <span className="meet-inline-edit-label">{dateLabelText}</span>
            {isVirtualMeet ? (
              <DatePicker.RangePicker
                value={[editMeetDateValue, editMeetEndDateValue]}
                disabledDate={(dateToCheck, info) => {
                  const dateValue = dateToCheck as Dayjs | null

                  if (!dateValue) {
                    return true
                  }

                  if (isDateOutsideAllowedMeetWindow(dateValue)) {
                    return true
                  }

                  if (info?.from && info.from.diff(dateValue, "day") > 0) {
                    return true
                  }

                  return false
                }}
                onOpenChange={(open) => setIsMeetDatePickerOpen(open)}
                onChange={(values) => {
                  setEditMeetDateValue(
                    values?.[0] ? dayjs(values[0].toDate()) : null,
                  )
                  setEditMeetEndDateValue(
                    values?.[1] ? dayjs(values[1].toDate()) : null,
                  )
                }}
                disabled={updateMeetDateTimeSaving}
                allowClear={false}
              />
            ) : (
              <DatePicker
                value={editMeetDateValue}
                disabledDate={(dateToCheck) =>
                  isDateOutsideAllowedMeetWindow(dateToCheck as Dayjs | null)
                }
                onOpenChange={(open) => setIsMeetDatePickerOpen(open)}
                onChange={(value) => setEditMeetDateValue(value)}
                disabled={updateMeetDateTimeSaving}
                allowClear={false}
              />
            )}
          </div>
          <div className="meet-inline-editing-row">
            <span style={{ marginRight: 6 }}>
              <FontAwesomeIcon icon={faClock} />
            </span>
            <span className="meet-inline-edit-label">Time: </span>
            <TimePicker
              use12Hours
              format="h:mm a"
              minuteStep={5}
              value={editMeetTimeValue}
              onOpenChange={(open) => setIsMeetTimePickerOpen(open)}
              onChange={(value) =>
                setEditMeetTimeValue(
                  value
                    ? dayjs()
                        .hour(value.hour())
                        .minute(value.minute())
                        .second(0)
                        .millisecond(0)
                    : null,
                )
              }
              disabled={updateMeetDateTimeSaving}
              allowClear={false}
            />
            <span className="meet-inline-edit-actions">
              <Button
                type="text"
                size="small"
                aria-label="Save meet date and time"
                style={{ color: "#389e0d" }}
                icon={<FontAwesomeIcon icon={faCircleCheck} />}
                loading={updateMeetDateTimeSaving}
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleUpdateMeetDateTime}
              />
              <Button
                type="text"
                size="small"
                aria-label="Cancel meet date and time edit"
                style={{ color: "#cf1322" }}
                icon={<FontAwesomeIcon icon={faCircleXmark} />}
                onMouseDown={(e) => e.preventDefault()}
                onClick={cancelMeetDateTimeEdit}
                disabled={updateMeetDateTimeSaving}
              />
            </span>
          </div>
        </div>
      )
    }

    return (
      <div
        ref={meetDateTimeAreaRef}
        className={`meet-inline-edit-display${showDateTimeTrigger ? " meet-inline-edit-visible" : ""}`}
        onClick={() => {
          if (canEditMeetDateTime && !showDateTimeTrigger) {
            setShowDateTimeTrigger(true)
          }
        }}
      >
        <div className="meet-inline-edit-display-row">
          <span style={{ marginRight: 6 }}>
            <FontAwesomeIcon icon={faCalendar} />
          </span>
          <span>
            <span className="meet-inline-edit-label">{dateLabelText}</span>
            <span className="meet-inline-edit-value">{dateLabel}</span>
          </span>
          {canEditMeetDateTime ? (
            seasonEnded ? seasonEndedEditIcon : (
            <Button
              type="link"
              className="meet-inline-edit-trigger"
              size="small"
              onClick={() => openMeetDateTimeEditor()}
              style={{ paddingInline: 0, height: "auto" }}
              aria-label="Edit meet date and time"
            >
              <FontAwesomeIcon icon={faPenToSquare} />
            </Button>
            )
          ) : null}
        </div>
        {formattedMeetTime ? (
          <div className="meet-inline-edit-display-row">
            <span style={{ marginRight: 6 }}>
              <FontAwesomeIcon icon={faClock} />
            </span>
            <span>
              <span className="meet-inline-edit-label">Time: </span>
              <span className="meet-inline-edit-value">{formattedMeetTime}</span>
            </span>
          </div>
        ) : null}
      </div>
    )
  }

  const renderMeetEntriesDeadlineOverview = () => {
    if (isVirtualMeet) {
      return null
    }

    if (isEditingMeetEntriesDeadline) {
      return (
        <div className="meet-inline-editing">
          <div className="meet-inline-editing-row" style={{ alignItems: "flex-start" }}>
            <span style={{ marginRight: 6 }}>
              <FontAwesomeIcon icon={faClock} />
            </span>
            <span className="meet-inline-edit-label">Meet Entries Deadline:</span>
            <DatePicker
              showTime={{ use12Hours: true, format: "h:mm a", minuteStep: 5 }}
              format="MMM D, YYYY h:mm a"
              value={editMeetEntriesDeadlineValue}
              onChange={(value) => setEditMeetEntriesDeadlineValue(value)}
              disabled={updateMeetEntriesDeadlineSaving}
              allowClear
              disabledDate={(current) => {
                if (!current) return false
                if (current.endOf("day").isBefore(meetEntriesDeadlineFloor)) {
                  return true
                }
                if (
                  meetStartDate?.isValid() &&
                  current.startOf("day").isAfter(meetStartDate.endOf("day"))
                ) {
                  return true
                }
                return false
              }}
              disabledTime={(selectedDate) => {
                if (
                  !selectedDate ||
                  !meetStartDateTime?.isValid() ||
                  !selectedDate.isSame(meetStartDateTime, "day")
                ) {
                  return {}
                }

                const maxHour = meetStartDateTime.hour()
                const maxMinute = meetStartDateTime.minute()

                return {
                  disabledHours: () =>
                    Array.from({ length: 24 }, (_, index) => index).filter(
                      (hour) => hour > maxHour,
                    ),
                  disabledMinutes: (selectedHour) =>
                    selectedHour === maxHour
                      ? Array.from({ length: 60 }, (_, index) => index).filter(
                          (minute) => minute > maxMinute,
                        )
                      : [],
                }
              }}
            />
            <span className="meet-inline-edit-actions">
              <Button
                type="text"
                size="small"
                aria-label="Save meet entries deadline"
                style={{ color: "#389e0d" }}
                icon={<FontAwesomeIcon icon={faCircleCheck} />}
                loading={updateMeetEntriesDeadlineSaving}
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleUpdateMeetEntriesDeadline}
              />
              <Button
                type="text"
                size="small"
                aria-label="Cancel meet entries deadline edit"
                style={{ color: "#cf1322" }}
                icon={<FontAwesomeIcon icon={faCircleXmark} />}
                onMouseDown={(e) => e.preventDefault()}
                onClick={cancelMeetEntriesDeadlineEdit}
                disabled={updateMeetEntriesDeadlineSaving}
              />
            </span>
          </div>
          <div className="meet-inline-editing-row" style={{ alignItems: "flex-start", marginLeft: 24 }}>
            <Checkbox
              checked={editAutoMarkHeatSheetReadyAfterDeadline}
              disabled={updateMeetEntriesDeadlineSaving || !editMeetEntriesDeadlineValue}
              onChange={(event) =>
                setEditAutoMarkHeatSheetReadyAfterDeadline(event.target.checked)
              }
            >
              Automatically set all clubs to heat sheet ready once the deadline passes
            </Checkbox>
          </div>
          <div
            style={{
              marginLeft: 24,
              fontSize: 12,
              color: "rgba(16, 18, 66, 0.72)",
              lineHeight: 1.45,
            }}
          >
            Leave the deadline blank to keep this optional. Meet host can manually mark clubs as Heat Sheet Ready after the deadline passes.
          </div>
        </div>
      )
    }

    return (
      <div
        ref={meetEntriesDeadlineAreaRef}
        className={`meet-inline-edit-display${showMeetEntriesDeadlineTrigger ? " meet-inline-edit-visible" : ""}`}
        onClick={() => {
          if (canOpenMeetEntriesDeadlineEditor && !showMeetEntriesDeadlineTrigger) {
            setShowMeetEntriesDeadlineTrigger(true)
          }
        }}
        onKeyDown={(event) => {
          if (
            (event.key === "Enter" || event.key === " ") &&
            canOpenMeetEntriesDeadlineEditor &&
            !showMeetEntriesDeadlineTrigger
          ) {
            event.preventDefault()
            setShowMeetEntriesDeadlineTrigger(true)
          }
        }}
        role={canOpenMeetEntriesDeadlineEditor ? "button" : undefined}
        tabIndex={canOpenMeetEntriesDeadlineEditor ? 0 : undefined}
      >
        <span className="meet-inline-edit-display-row" style={{ flexWrap: "wrap", rowGap: 6 }}>
          <span style={{ marginRight: 6 }}>
            <FontAwesomeIcon icon={faClock} />
          </span>
          <span>
            <span style={{ fontWeight: 400 }}>Meet Entries Deadline: </span>
            <span style={{ fontWeight: 600 }}>
              {meetEntriesDeadlineValue
                ? meetEntriesDeadlineValue.format("MMM D, YYYY h:mm A")
                : "Not set"}
            </span>
          </span>
          <Button
            type="link"
            size="small"
            onClick={() => setMeetEntriesDeadlineInfoVisible(true)}
            style={{ paddingInline: 4, height: "auto" }}
            aria-label="About meet entries deadline"
          >
            <InfoCircleOutlined />
          </Button>
          {meetEntriesDeadlineValue ? (
            <Tag color={hasMeetEntriesDeadlinePassed ? "orange" : "blue"}>
              {hasMeetEntriesDeadlinePassed ? "Deadline Passed" : "Deadline Active"}
            </Tag>
          ) : null}
          {meetEntriesDeadlineValue ? (
            <Tag color={autoMarkHeatSheetReadyAfterDeadline ? "green" : "gold"}>
              {autoMarkHeatSheetReadyAfterDeadline
                ? "Auto-set clubs heat sheet ready"
                : "Manual review after deadline"}
            </Tag>
          ) : null}
          {canEditMeetEntriesDeadline && !hasSelectableMeetEntriesDeadlineDate ? (
            <Tag color="default">Meet date has passed</Tag>
          ) : null}
          {canOpenMeetEntriesDeadlineEditor ? (
            seasonEnded ? seasonEndedEditIcon : (
            <Button
              type="link"
              className="meet-inline-edit-trigger"
              size="small"
              onClick={(event) => {
                event.stopPropagation()
                openMeetEntriesDeadlineEditor()
              }}
              style={{ paddingInline: 0, height: "auto" }}
              aria-label="Edit meet entries deadline"
            >
              <FontAwesomeIcon icon={faPenToSquare} />
            </Button>
            )
          ) : null}
        </span>
      </div>
    )
  }

  const renderMeetEntriesDeadlineStatusSummary = () => {
    if (!canManageHeatSheetDeadlineOverrides) {
      return null
    }

    return (
      <div
        style={{
          padding: "12px 14px",
          borderRadius: 12,
          background: autoMarkHeatSheetReadyAfterDeadline
            ? "rgba(82, 196, 26, 0.08)"
            : "rgba(250, 173, 20, 0.10)",
          border: autoMarkHeatSheetReadyAfterDeadline
            ? "1px solid rgba(82, 196, 26, 0.24)"
            : "1px solid rgba(250, 173, 20, 0.28)",
        }}
      >
        <div style={{ fontWeight: 700, color: "#101242", marginBottom: 4 }}>
          Meet Entries Deadline has passed
        </div>
        <div style={{ fontSize: 12, color: "rgba(16, 18, 66, 0.84)", lineHeight: 1.5 }}>
          Entries Completed {heatSheetReadyCount}/{trackedClubCount}
        </div>
        <div style={{ fontSize: 12, color: "rgba(16, 18, 66, 0.72)", lineHeight: 1.5, marginTop: 4 }}>
          {autoMarkHeatSheetReadyAfterDeadline
            ? pendingHeatSheetReadyCount > 0
              ? "Automatic completion is enabled. Any clubs still behind will be advanced when eligible users open this meet."
              : "Automatic completion is enabled and all clubs are now ready for heat sheet merge."
            : pendingHeatSheetReadyCount > 0
              ? "Click 'Entries Completed' on the tracker below to mark each club complete."
              : "All clubs have completed their entries — ready to confirm heat & lane assignments and merge."}
        </div>
      </div>
    )
  }

  const renderHeatSheetTrackerAction = (
    clubSeason: IClubSeasonInformation,
  ) => {
    if (!canManageHeatSheetDeadlineOverrides) {
      return null
    }

    if (clubSeason.confirmLineup) {
      return null
    }

    return {
      onClick: () =>
        handleMarkMeetClubHeatSheetReadyAfterDeadline(clubSeason.club.clubId || 0),
      loading: updatingHeatSheetReadyClubId === clubSeason.club.clubId,
    }
  }

  const SEASON_ENDED_REASON = "This season has ended. This action is no longer available."

  // Helper: wraps a menu item label in a Tooltip when the season has ended.
  const seasonMenuLabel = (icon: React.ReactNode, text: string) => {
    const inner = (
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {icon}
        <span style={{ marginLeft: 8 }}>{text}</span>
      </span>
    )
    return seasonEnded ? <Tooltip title={SEASON_ENDED_REASON}>{inner}</Tooltip> : inner
  }

  const actionsMenuItems: MenuProps["items"] = [
    {
      type: "group",
      label: "Setup",
      children: [
        ...(canEditMyMeetCourse
          ? [
              {
                key: "update-my-meet-course",
                disabled: seasonEnded,
                label: seasonMenuLabel(<FontAwesomeIcon icon={faWaterLadder} />, "Update my meet course"),
              },
            ]
          : []),
        ...(canUpdateVirtualCourse
          ? [
              {
                key: "update-virtual-course",
                disabled: seasonEnded,
                label: seasonMenuLabel(<FontAwesomeIcon icon={faWaterLadder} />, "Update virtual meet course"),
              },
            ]
          : []),
        ...(canCloneMeet
          ? [
              {
                key: "clone-meet",
                disabled: seasonEnded,
                label: seasonMenuLabel(<FontAwesomeIcon icon={faCopy} />, "Clone meet"),
              },
            ]
          : []),
        ...(canEditParticipatingClubs
          ? [
              {
                key: "update-participating-clubs",
                disabled: seasonEnded,
                label: seasonMenuLabel(<FontAwesomeIcon icon={faUsers} />, "Update participating clubs"),
              },
            ]
          : []),
      ],
    },
    ...(() => {
      const canImportResults = (meetStatus !== 2 || meetResultSource === 'fileImport') && !seasonEnded
      const showImports = canImportEntries || canImportResults || seasonEnded
      return showImports ? [{ type: "divider" as const }] : []
    })(),
    ...(() => {
      const canImportResults = (meetStatus !== 2 || meetResultSource === 'fileImport') && !seasonEnded
      const showImportEntries = canImportEntries || seasonEnded
      const showImportResults = canImportResults || seasonEnded
      const children = [
        ...(showImportEntries
          ? [
              {
                key: "import-entries",
                disabled: seasonEnded || sd3ImportDeactivated,
                label: seasonMenuLabel(<FontAwesomeIcon icon={faFileImport} />, "Import entries (HY3/CL2/SD3)"),
              },
            ]
          : []),
        ...(showImportResults
          ? [
              {
                key: "import-results",
                disabled: seasonEnded,
                label: seasonMenuLabel(<FontAwesomeIcon icon={faFileImport} />, "Import results (HY3/CL2/SD3)"),
              },
            ]
          : []),
      ]
      return children.length > 0
        ? [{ type: "group" as const, label: "Imports", children }]
        : []
    })(),
    ...(meetSessionContext?.multiSessionMeetId
      ? [
          { type: "divider" as const },
          {
            type: "group" as const,
            label: "Multi-Session Series",
            children: [
              {
                key: "export-all-sessions",
                label: seasonMenuLabel(
                  <FontAwesomeIcon icon={faFileImport} />,
                  "Export all sessions entries (HY3/CL2)",
                ),
              },
            ],
          },
        ]
      : []),
    ...(showDeleteMeetAction
      ? [
          { type: "divider" as const },
          {
            key: "delete-meet",
            danger: true,
            label: (
              <span
                style={{ display: "inline-flex", alignItems: "center" }}
              >
                <FontAwesomeIcon icon={faTrash} />
                <span style={{ marginLeft: 8 }}>Delete meet</span>
              </span>
            ),
          },
        ]
      : []),
  ]

  const handleActionsMenuClick: MenuProps["onClick"] = ({ key }) => {
    switch (key) {
      case "update-my-meet-course":
        if (canEditMyMeetCourse && !seasonEnded) setUpdateMyMeetCourseVisible(true)
        break
      case "update-virtual-course":
        if (canUpdateVirtualCourse && !seasonEnded) setUpdateVirtualCourseVisible(true)
        break
      case "update-participating-clubs":
        if (canEditParticipatingClubs && !seasonEnded) setUpdateParticipatingClubsVisible(true)
        break
      case "clone-meet":
        if (canCloneMeet && !seasonEnded) cloneThisMeet()
        break
      case "import-entries":
        if (canImportEntries && !sd3ImportDeactivated && !seasonEnded) {
          setVisible(false)
          setImportEntriesVisible(true)
        }
        break
      case "import-results":
        if (!seasonEnded) {
          setVisible(false)
          setImportResultsVisible(true)
        }
        break
      case "export-all-sessions":
        if (meetSessionContext?.multiSessionMeetId) {
          window.location.href = gen(
            `/api/meetEntriesExportAllSessions/${meetSessionContext.multiSessionMeetId}`,
          )
        }
        break
      case "delete-meet":
        if (showDeleteMeetAction) {
          openDeleteMeetModal()
        }
        break
      default:
        break
    }
  }

  const showEditEntriesPrimary = thisClubStatus !== 1 && thisClubStatus !== 2
  const showHeatSheetPrimary = thisClubStatus === 1
  const showResultsPrimary = thisClubStatus === 2

  const reportStudioContext: "entries" | "heatSheet" | "results" =
    thisClubStatus === 1 ? "heatSheet" : thisClubStatus === 2 ? "results" : "entries"

  useEffect(() => {
    let mounted = true
    if (visible && meetId !== -1) {
      setEventOrderLoading(true)
      swimminglyApi
        .get(gen(`/api/getMeetEventOrder/${meetId}`))
        .then((data: { meetEvents: TMeetEvent[] }) => {
          if (!mounted) return
          setMeetEvents(data.meetEvents || [])
        })
        .catch(() => {
          if (!mounted) return
          setMeetEvents([])
        })
        .finally(() => {
          if (mounted) setEventOrderLoading(false)
        })
    }
    return () => {
      mounted = false
    }
  }, [visible, meetId, triggerRefresh])

  const handleReportStudioDownloadHeatSheetPdf = useCallback(
    async (numColumns: number, options?: any) => {
      if (!impersonatedClubId) {
        message.error("Missing club information for heat sheet download.")
        return
      }

      const safeColumns = numColumns === 2 ? 2 : 3

      try {
        const response = await fetch(
          gen(
            `/api/getHeatSheetJson/meet=${meetId}/club=${impersonatedClubId}`,
          ),
          {
            method: "get",
            headers: { "Content-Type": "application/json" },
            credentials: "same-origin",
          },
        )

        if (!response.ok) {
          throw new Error(`Failed to fetch heat sheet data: ${response.status}`)
        }

        const data = await response.json()

        if (!data.heatSheetData || !Array.isArray(data.heatSheetData.events)) {
          throw new Error(
            "Invalid heat sheet data returned from server (missing events)",
          )
        }

        generateHeatSheetPDF(data.heatSheetData, null, safeColumns, options || {})
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error generating heat sheet PDF from modal", err)
        message.error("Problem generating heat sheet PDF. Please try again.")
      }
    },
    [impersonatedClubId, meetId],
  )

  const renderFooter = () => {
    if (meetInfoLoading) {
      return null
    }

    const createReportButton =
      activeTab === "reportStudio" && inlineCreateReportLabel ? (
        <Button
          key="create-report"
          className="shadowed item"
          onClick={() => {
            void inlineCreateReportHandlerRef.current?.()
          }}
        >
          {inlineCreateReportLabel}
        </Button>
      ) : null

    // Results phase with virtual meet: highlight virtual results, keep club results secondary
    if (showResultsPrimary && virtualMeetId !== null && virtualMeetHasResults) {
      return (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          {createReportButton}
          <Button
            key="virtual-results"
            className="shadowed item primary-gradient-button"
            type="primary"
            onClick={() =>
              history.push(`/app/virtualMeet/results/${virtualMeetId}`)
            }
          >
            <FontAwesomeIcon icon={faEye} /> View Virtual Results
          </Button>
          <Button
            key="club-results"
            className="shadowed item"
            onClick={() => history.push(`/app/meet/results/${meetId}`)}
          >
            <FontAwesomeIcon icon={faEye} /> View My Meet Results
          </Button>
        </div>
      )
    }

    if (showResultsPrimary) {
      return (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          {createReportButton}
          <Button
            key="club-results"
            className="shadowed item primary-gradient-button"
            type="primary"
            onClick={() => history.push(`/app/meet/results/${meetId}`)}
          >
            <FontAwesomeIcon icon={faEye} /> View My Meet Results
          </Button>
        </div>
      )
    }

    if (showHeatSheetPrimary) {
      return (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          {createReportButton}
          {!hideViewHeatSheetButton && (
            <Button
              key="heat-sheet"
              className="shadowed item primary-gradient-button"
              type="primary"
              onClick={() => {
                history.push(`/app/club/heatSheet2/${meetId}`)
              }}
            >
              <FontAwesomeIcon icon={faFileCircleCheck} /> View Heat Sheet
            </Button>
          )}
        </div>
      )
    }

    if (showEditEntriesPrimary && !hideEditEntriesButton) {
      return (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          {createReportButton}
          <Button
            key="edit-entries"
            className="shadowed item primary-gradient-button"
            type="primary"
            onClick={() => setMeetLineupConfirmationVisible(true)}
          >
            <FontAwesomeIcon icon={faPenToSquare} /> Edit Entries
          </Button>
        </div>
      )
    }

    if (createReportButton) {
      return (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          {createReportButton}
        </div>
      )
    }

    return null
  }

  const reportStudioMeetInfo = useMemo(
    () => ({
      unit_of_measure:
        unitOfMeasure !== null
          ? String(unitOfMeasure)
          : courseLabel === "SCY"
            ? "0"
            : courseLabel === "SCM"
              ? "1"
              : "2",
      meet_date: meetStartDate.toISOString(),
      individualLimit: editIndividualEntryLimit,
      relayLimit: editRelayEntryLimit,
      totalLimit: editTotalEntryLimit,
    }),
    [courseLabel, meetStartDate, unitOfMeasure, editIndividualEntryLimit, editRelayEntryLimit, editTotalEntryLimit],
  )

  const reportStudioMeetEvents = useMemo(
    () =>
      meetEvents.map((evt) => ({
        id: evt.swimEventId,
        swimEventId: evt.swimEventId,
        eventNumber: evt.eventNumber,
        event_sort_order: evt.eventNumber,
        displayNumber: evt.displayNumber,
        display_number: evt.displayNumber,
        division: evt.division,
        gender: evt.gender,
        age_group: evt.ageGroup,
        distance: evt.distance,
        stroke: evt.stroke,
      })),
    [meetEvents],
  )

  const reportStudioClub = useMemo(
    () =>
      impersonateClub
        ? {
            ...impersonateClub,
            entriesCount: thisClubSeason?.entriesCount,
          }
        : null,
    [impersonateClub, thisClubSeason?.entriesCount],
  )

  const handleOpenReportStudioExportEntries = useCallback(
    (clubIds: Array<number | string>) => {
      setExportSelectedClubIds(clubIds)
      setExportEntriesModalVisible(true)
    },
    [],
  )

  const handleOpenReportStudioExportResults = useCallback(() => {
    setExportResultsVisible(true)
  }, [])

  const handleInlineCreateReportChange = useCallback(
    (nextConfig: { label: string; onClick: () => void | Promise<void> }) => {
      setInlineCreateReportLabel(nextConfig.label)
      inlineCreateReportHandlerRef.current = nextConfig.onClick
    },
    [],
  )

  const reportStudioTabContent = useMemo(
    () => (
      <SwimMeetReportStudio
        inline
        visible
        onClose={() => {}}
        onDownloadHeatSheetPdf={handleReportStudioDownloadHeatSheetPdf}
        meetInfoArr={reportStudioMeetInfo}
        meetEventsArr={reportStudioMeetEvents}
        meetName={meetName}
        club={reportStudioClub}
        meetId={meetId}
        user={user || null}
        context={reportStudioContext}
        onOpenExportEntries={handleOpenReportStudioExportEntries}
        onOpenExportResults={handleOpenReportStudioExportResults}
        virtualMeetId={virtualMeetId}
        resultsPdfExists={Boolean(meetPdfStatus?.resultsExists)}
        labelsPdfExists={Boolean(meetPdfStatus?.labelsExists)}
        onInlineCreateReportChange={handleInlineCreateReportChange}
      />
    ),
    [
      handleInlineCreateReportChange,
      handleOpenReportStudioExportEntries,
      handleOpenReportStudioExportResults,
      handleReportStudioDownloadHeatSheetPdf,
      meetId,
      meetName,
      meetPdfStatus?.labelsExists,
      meetPdfStatus?.resultsExists,
      reportStudioClub,
      reportStudioContext,
      reportStudioMeetEvents,
      reportStudioMeetInfo,
      user,
      virtualMeetId,
    ],
  )

  return (
    <div>
      <StyledModal
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <span className="meet-title-primary">
              <span className="meet-title-label">Meet:</span>
              {isRenamingMeet ? (
                <>
                  {renderRenameField("desktop-only")}
                  <span className="mobile-only">
                    <b>{meetName.length > 0 ? meetName : "NO MEET NAME"}</b>
                  </span>
                </>
              ) : (
                <span
                  ref={meetNameDisplayRef}
                  className={`meet-name-display${showRenameTrigger ? " meet-name-edit-visible" : ""}`}
                  onClick={() => {
                    if (canRenameMeet && !showRenameTrigger) {
                      setShowRenameTrigger(true)
                    }
                  }}
                >
                  <b>{meetName.length > 0 ? meetName : "NO MEET NAME"}</b>
                  {canRenameMeet ? (
                    seasonEnded ? seasonEndedEditIcon : (
                    <Button
                      type="link"
                      className="meet-name-edit-trigger"
                      size="small"
                      onClick={() => {
                        setShowRenameTrigger(true)
                        setRenameMeetValue(meetName)
                        setIsRenamingMeet(true)
                      }}
                      style={{ paddingInline: 0, height: "auto" }}
                      aria-label="Edit meet name"
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </Button>
                    )
                  ) : null}
                </span>
              )}
            </span>
            <span style={{ marginLeft: 8 }}>
              <Tag color={isVirtualMeet ? "geekblue" : "default"}>
                {isVirtualMeet ? "Virtual Swim Meet" : "In-Person Swim Meet"}
              </Tag>
            </span>
          </div>
        }
        width="100%"
        style={{ top: 32 }}
        destroyOnHidden
        open={visible}
        afterOpenChange={(open) => {
          if (!open) return
          resetPageScroll(document, window)
        }}
        onCancel={() => setVisible(false)}
        footer={renderFooter()}
      >
        <div className="modal-body">
          {meetInfoLoading ? (
            <div style={{ padding: "12px 8px" }}>
              <div className="meet-actions-skeleton-overview-card">
                <div className="meet-actions-skeleton-overview-grid">
                  <Skeleton.Button active size="small" block />
                  <Skeleton.Button active size="small" block />
                  <Skeleton.Button active size="small" block />
                  <Skeleton.Button active size="small" block />
                </div>
                <Skeleton active paragraph={{ rows: 2 }} title={{ width: "35%" }} />
              </div>

              <div className="meet-actions-skeleton-tab-row">
                <div className="meet-actions-skeleton-tab-list">
                  <Skeleton.Button active size="small" style={{ width: 220 }} />
                  <Skeleton.Button active size="small" style={{ width: 180 }} />
                  <Skeleton.Button active size="small" style={{ width: 140 }} />
                </div>
                <Skeleton.Button active size="small" style={{ width: 90 }} />
              </div>

              <div className="meet-actions-skeleton-content-card">
                <Skeleton active paragraph={{ rows: 7 }} title={{ width: "42%" }} />
              </div>
            </div>
          ) : (
            <>
              {isRenamingMeet ? (
                <div className="meet-name-edit-panel mobile-only">
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#666" }}>
                    Edit meet name
                  </div>
                  {renderRenameField()}
                </div>
              ) : null}
              <MeetOverviewTab
                isVirtualMeet={isVirtualMeet}
                dateLabel={dateLabel}
                formattedMeetTime={formattedMeetTime}
                dateTimeOverviewContent={renderMeetDateTimeOverview()}
                meetEntriesDeadlineContent={renderMeetEntriesDeadlineOverview()}
                deadlineStatusSummaryContent={renderMeetEntriesDeadlineStatusSummary()}
                courseLabel={courseLabel}
                virtualCourseLabel={virtualCourse}
                myMeetCourseLabel={course}
                individualEntryLimit={individualEntryLimit}
                relayEntryLimit={relayEntryLimit}
                totalEntryLimit={totalEntryLimit}
                scoringSummaryText={scoringSummaryText}
                numClubsInMeet={numClubsInMeet}
                virtualMeetId={virtualMeetId}
                hostClub={hostClub as any}
                meetPool={meetPool}
                meetLocation={meetLocation}
                meetClubSeasons={meetClubSeasons}
                homeClubId={homeClubId}
                meetStatus={meetStatus}
                meetId={meetId}
                meetDivisions={meetDivisions}
                canEditMeetPool={canEditMeetPool}
                onEditMeetPool={() => setUpdateTraditionalCourseVisible(true)}
                canEditHostClub={canEditHostClub}
                onEditHostClub={() => setUpdateHostClubVisible(true)}
                onHostClubChanged={refreshMeetAndDeclarations}
                canEditVirtualCourse={canUpdateVirtualCourse}
                onEditVirtualCourse={() => setUpdateVirtualCourseVisible(true)}
                canEditMyMeetCourse={canEditMyMeetCourse}
                onEditMyMeetCourse={() => setUpdateMyMeetCourseVisible(true)}
                seasonEnded={seasonEnded}
                entryLimitsOverviewContent={renderEntryLimitsOverviewContent()}
                renderHeatSheetTrackerAction={renderHeatSheetTrackerAction}
              />
              <Tabs
                type="card"
                activeKey={activeTab}
                onChange={(key) => {
                  if (key === "declarations" && isFeatureLocked(FeatureKey.declarations)) {
                    openLockedModal(FeatureKey.declarations)
                    return
                  }
                  setActiveTab(
                    key as "reportStudio" | "declarations" | "eventOrder" | "activityLog",
                  )
                }}
                tabBarExtraContent={
                  <Dropdown
                    menu={{ items: actionsMenuItems, onClick: handleActionsMenuClick }}
                    trigger={["hover"]}
                    placement="bottomRight"
                  >
                    <Button type="link">
                      Actions <DownOutlined />
                    </Button>
                  </Dropdown>
                }
                items={[
              {
                key: "reportStudio",
                label: "Swim Meet Report Studio",
                children: reportStudioTabContent,
              },
              {
                key: "declarations",
                disabled: meetSessionContext?.sessionType === "finals" || !supportsDeclarations,
                label: meetSessionContext?.sessionType === "finals" ? (
                  <Tooltip title="Not available for a finals session">
                    <span style={{ opacity: 0.45 }}>Meet Declarations</span>
                  </Tooltip>
                ) : supportsDeclarations ? (
                  <span style={{ display: "inline-flex", alignItems: "center" }}>
                    <span>Meet Declarations</span>
                    <span
                      style={{
                        marginLeft: 6,
                        padding: "0 6px",
                        borderRadius: 10,
                        fontSize: 10,
                        fontWeight: 600,
                        backgroundColor: "#52c41a",
                        color: "white",
                        lineHeight: "16px",
                      }}
                    >
                      NEW
                    </span>
                  </span>
                ) : (
                  <span style={{ opacity: 0.6 }}>Meet Declarations (Unavailable)</span>
                ),
                children: supportsDeclarations ? (
                  <MeetDeclarationsTab
                    meetId={meetId}
                    meetName={meetName}
                    dateLabel={dateLabel}
                    courseLabel={courseLabel}
                    impersonateClubName={
                      impersonateClub?.nameLong || impersonateClub?.name || ""
                    }
                    impersonateClubId={impersonateClub?.clubId ?? null}
                    userId={user?.userId ?? null}
                    meetStatus={
                      seasonEnded && (declarationsMeetStatus === null || declarationsMeetStatus === 0)
                        ? 1
                        : declarationsMeetStatus
                    }
                    declarationsPhase={declarationsPhase}
                    meetDeclarationsCloseDate={meetDeclarationsCloseDate}
                    parentEntries={parentEntries}
                    parentAllowIndividualEntries={parentAllowIndividualEntries}
                    parentAllowRelayAvailability={parentAllowRelayAvailability}
                    parentAllowComments={parentAllowComments}
                    declarationsReportLoading={declarationsReportLoading}
                    declarationsReportSorted={declarationsReportSorted}
                    declarationsReportSwimmers={declarationsReportSwimmers}
                    onRefreshTrigger={refreshMeetScheduleAndDeclarations}
                    meetDeadlineUpperIso={declarationsDeadlineUpperIso}
                  />
                ) : (
                  <div
                    style={{
                      padding: "24px",
                      textAlign: "center",
                      color: "#666",
                    }}
                  >
                    <p style={{ fontSize: "14px", marginBottom: "12px" }}>
                      <strong>Meet Declarations Reporting</strong>
                    </p>
                    <p style={{ fontSize: "13px" }}>
                      Meet declarations reporting is only available for meets created after v5.0.9.
                      <br />
                      Check the 'Updates' tab in your profile drawer for more info.
                    </p>
                  </div>
                ),
              },
              {
                key: "eventOrder",
                label: (
                  <span style={{ display: "inline-flex", alignItems: "center" }}>
                    <span>Event Order</span>
                    <span
                      style={{
                        marginLeft: 6,
                        padding: "0 6px",
                        borderRadius: 10,
                        fontSize: 10,
                        fontWeight: 600,
                        backgroundColor: "#52c41a",
                        color: "white",
                        lineHeight: "16px",
                      }}
                    >
                      NEW
                    </span>
                  </span>
                ),
                children: (
                  <MeetEventOrderTab
                    meetId={meetId}
                    meetName={meetName}
                    courseLabel={course}
                    meetStartDateIso={meetStartDate.toISOString()}
                    meetEndDateIso={meetEndDate.toISOString()}
                    facilityNameDefault={
                      (meetPool && meetPool.pool_name) || meetLocation || meetName
                    }
                    eventOrderName={eventOrderName}
                    eventOrderLoading={eventOrderLoading}
                    meetEvents={meetEvents}
                    savedIndividualLimit={editIndividualEntryLimit}
                    savedRelayLimit={editRelayEntryLimit}
                    savedTotalLimit={editTotalEntryLimit}
                  />
                ),
              },
              {
                key: "activityLog",
                label: "Activity Log",
                children: (
                  <MeetActivityLogTab
                    meetId={meetId}
                    meetName={meetName}
                    meetStatus={meetStatus}
                    isVirtualMeet={Boolean(virtualMeetId)}
                    meetClubSeasons={meetClubSeasons}
                    refreshKey={triggerRefresh}
                  />
                ),
              },
                ]}
              />
            </>
          )}
        </div>
      </StyledModal>

      {impersonateClub?.clubId && (
        <ExportResultsModal
          meetId={meetId}
          clubId={impersonateClub.clubId}
          isVisible={exportResultsVisible}
          updateVisible={setExportResultsVisible}
          meetSessionContext={meetSessionContext}
        />
      )}

      <ImportResultsModal
        visible={importResultsVisible}
        setVisible={setImportResultsVisible}
        meetId={meetId}
        refresh={refreshMeetAndDeclarations}
      />
      <ImportEntriesModal
        visible={importEntriesVisible}
        setVisible={setImportEntriesVisible}
        meetId={meetId}
        refresh={refreshMeetAndDeclarations}
        individualEntryLimit={individualEntryLimit}
        relayEntryLimit={relayEntryLimit}
        totalEntryLimit={totalEntryLimit}
      />
      <Modal
        title="Remove Meet"
        open={deleteMeetModalVisible}
        onCancel={() => {
          if (!deleteMeetSubmitting) {
            setDeleteMeetModalVisible(false)
          }
        }}
        onOk={handleDeleteMeet}
        okText="Remove Meet"
        okButtonProps={{
          danger: true,
          disabled:
            deleteMeetPreviewLoading ||
            !deleteMeetPreview?.permissions.canDelete,
        }}
        confirmLoading={deleteMeetSubmitting}
      >
        {deleteMeetPreviewLoading ? (
          <Skeleton active paragraph={{ rows: 5 }} />
        ) : deleteMeetPreview ? (
          <div style={{ display: "grid", gap: 12 }}>
            <div>
              <strong>{deleteMeetPreview.meet.meetName}</strong>
              <div style={{ marginTop: 4, color: "rgba(0, 0, 0, 0.65)" }}>
                {dayjs(deleteMeetPreview.meet.meetDate).format("MM-DD-YYYY")}
                {deleteMeetPreview.meet.hostClubName
                  ? ` · Host: ${deleteMeetPreview.meet.hostClubName}`
                  : ""}
              </div>
            </div>
            <div style={{ color: "rgba(0, 0, 0, 0.72)", lineHeight: 1.6 }}>
              Removed meets are no longer accessible in the Clubhouse and are not available to be sync'd with your Scorekeeper's iPad.
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              <div>
                <strong>{deleteMeetPreview.impact.teamCount}</strong> clubs are currently in this meet.
              </div>
              <div>
                <strong>{deleteMeetPreview.impact.entryCount}</strong> entries would be affected across <strong>{deleteMeetPreview.impact.clubsWithEntriesCount}</strong> clubs.
              </div>
              <div>
                <strong>{deleteMeetPreview.impact.confirmLineupCount}</strong> clubs have confirmed lineups and <strong>{deleteMeetPreview.impact.heatSheetAcceptedCount}</strong> clubs have accepted heat sheets.
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  marginBottom: 6,
                  textTransform: "uppercase",
                  color: "rgba(0, 0, 0, 0.55)",
                }}
              >
                Clubs affected
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                {deleteMeetPreview.impact.activeClubs.map((club) => (
                  <div
                    key={`delete-meet-preview-club-${club.clubId}`}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      padding: "8px 10px",
                      borderRadius: 8,
                      background: "rgba(0, 0, 0, 0.03)",
                    }}
                  >
                    <span>{club.clubName}</span>
                    <span style={{ color: "rgba(0, 0, 0, 0.55)" }}>
                      {club.entryCount} entries
                    </span>
                  </div>
                ))}
              </div>
            </div>
            {!deleteMeetPreview.permissions.canDelete ? (
              <div
                style={{
                  padding: "10px 12px",
                  borderRadius: 8,
                  background: "rgba(255, 77, 79, 0.08)",
                  color: "#a8071a",
                  lineHeight: 1.5,
                }}
              >
                {deleteMeetPreview.permissions.reason}
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
      <Modal
        open={meetEntriesDeadlineConfirmationVisible}
        title="Confirm Meet Entries Deadline Update"
        onCancel={() => {
          if (!updateMeetEntriesDeadlineSaving) {
            setMeetEntriesDeadlineConfirmationVisible(false)
          }
        }}
        onOk={confirmMeetEntriesDeadlineUpdate}
        okText="Confirm deadline update"
        confirmLoading={updateMeetEntriesDeadlineSaving}
        cancelButtonProps={{ disabled: updateMeetEntriesDeadlineSaving }}
      >
        <div style={{ display: "grid", gap: 12 }}>
          <div>
            {meetEntriesDeadlineImpactPreview?.impactedClubCount === 1
              ? "This meet entries deadline will shorten 1 club's meet declarations deadline."
              : `This meet entries deadline will shorten ${meetEntriesDeadlineImpactPreview?.impactedClubCount || 0} clubs' meet declarations deadlines.`}
          </div>
          <div>
            New meet entries deadline:{" "}
            <strong>
              {formatMeetEntriesDeadlineImpactDate(
                meetEntriesDeadlineImpactPreview?.meetEntriesDeadline || null,
              )}
            </strong>
          </div>
          <div style={{ maxHeight: 280, overflowY: "auto", display: "grid", gap: 8 }}>
            {(meetEntriesDeadlineImpactPreview?.clubs || []).map((club) => {
              const clubName =
                club.clubNameLong || club.clubNameShort || `Club ${club.clubId}`

              return (
                <div
                  key={club.clubId}
                  style={{
                    border: "1px solid rgba(16, 18, 66, 0.12)",
                    borderRadius: 8,
                    padding: 12,
                    background: club.willClamp ? "rgba(255, 244, 229, 0.75)" : "#fff",
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{clubName}</div>
                  <div style={{ fontSize: 12, color: "rgba(16, 18, 66, 0.72)", marginTop: 4 }}>
                    Current declarations deadline: {formatMeetEntriesDeadlineImpactDate(club.currentMeetDeclarationsCloseDate)}
                  </div>
                  <div style={{ fontSize: 12, color: "rgba(16, 18, 66, 0.72)", marginTop: 2 }}>
                    Result after save: {formatMeetEntriesDeadlineImpactDate(club.nextMeetDeclarationsCloseDate)}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, marginTop: 6 }}>
                    {club.willClamp ? "Will be shortened to match the meet entries deadline" : "No deadline change"}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </Modal>
      <Modal
        open={meetEntriesDeadlineInfoVisible}
        title="Meet Entries Deadline & Confirmation"
        onCancel={() => setMeetEntriesDeadlineInfoVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setMeetEntriesDeadlineInfoVisible(false)}>
            Close
          </Button>,
        ]}
      >
        <p>
          <strong>What is the meet entries deadline?</strong>
          {" "}The meet entries deadline is the time by which all clubs should finish submitting their entries.
          Only the meet host or a league admin can set it. We recommend setting it 24 hours before the meet starts. 
          <p><i>Once entries are confirmed for each club and the entry deadline has passed, entries are locked and cannot be modified for any club, even by the host or league admin. </i>
          </p>
        </p>
        <p>
          <strong>Club self-confirmation:</strong>
          {" "}Any club admin/coach should mark their own entries complete.
          Entries can always be changed here until the heat sheet is merged.
        </p>
        <p>
          <strong>Host confirmation (for another club):</strong>
        </p>
        <ul style={{ paddingLeft: 20 }}>
          <li>
            <strong>With a deadline:</strong> The host can confirm any club's entries after the deadline passes, and proceed to build the heat sheet.
            Before it passes, only the club itself can confirm its own entries.
          </li>
          <li style={{ marginTop: 6 }}>
            <strong>Without a deadline:</strong> The host can confirm a club if (a) the club has previously
            confirmed entries at least once, or (b) the meet starts within 24 hours.
            Otherwise the club must confirm entries on their own first.
          </li>
        </ul>
        <p style={{ marginBottom: 0 }}>
          <strong>Automatic completion:</strong> If "Auto-mark entries complete" is enabled, all clubs are
          automatically marked complete when the deadline passes — no manual action needed.
        </p>
      </Modal>
      <Modal
        open={meetLineupConfirmationVisible}
        title={
          <span>
            <ExclamationCircleTwoTone /> Heading to Meet Entries for your Lineup
          </span>
        }
        onCancel={() => setMeetLineupConfirmationVisible(false)}
        okText="Proceed to Meet Entries"
        onOk={() => history.push(`/app/club/viewMeetLineUp2/${meetId}`)}
      >
        <ul>
          <li>Proceeding to your club's live meet entries.</li>
          <li>
            Double-check the event order is accurate before completing your
            entries. If it is not accurate, please contact your club or league
            rep.
          </li>
        </ul>
      </Modal>

      <RegularMeetCourseModal
        visible={updateTraditionalCourseVisible}
        setVisible={setUpdateTraditionalCourseVisible}
        meetId={meetId}
        clubSeason={thisClubSeason}
        refresh={refreshMeetAndDeclarations}
        meetClubSeasons={
          isVirtualMeet
            ? meetClubSeasons.filter(
                (clubSeason) => clubSeason.club.clubId === meetClubId,
              )
            : meetClubSeasons
        }
      />
      <UpdateMeetHomeClubTraditional
        visible={updateHostClubVisible && !isVirtualMeet}
        setVisible={setUpdateHostClubVisible}
        meetId={meetId}
        homeClubId={homeClubId}
        meetClubSeasons={meetClubSeasons}
        refresh={refreshMeetAndDeclarations}
      />
      <UpdateMeetParticipatingClubsTraditional
        visible={updateParticipatingClubsVisible && !isVirtualMeet}
        setVisible={setUpdateParticipatingClubsVisible}
        meetId={meetId}
        homeClubId={homeClubId}
        meetClubSeasons={meetClubSeasons}
        refresh={refreshMeetScheduleAndDeclarations}
      />
      {virtualMeetId !== null ? (
        <UpdateMeetParticipatingClubsVirtual
          visible={updateParticipatingClubsVisible && isVirtualMeet}
          setVisible={setUpdateParticipatingClubsVisible}
          meetId={meetId}
          virtualMeetId={virtualMeetId}
          homeClubId={homeClubId}
          meetClubSeasons={meetClubSeasons}
          refresh={refreshMeetScheduleAndDeclarations}
        />
      ) : null}
      <UpdateMeetCourseMyMeet
        visible={updateMyMeetCourseVisible}
        setVisible={setUpdateMyMeetCourseVisible}
        clubSeason={thisClubSeason}
        meetId={meetId}
        currentCourse={course}
        refresh={refreshMeetAndDeclarations}
      />
      <VirtualMeetCourseModal
        visible={updateVirtualCourseVisible}
        setVisible={setUpdateVirtualCourseVisible}
        clubSeason={thisClubSeason}
        virtualMeetId={virtualMeetId}
        currentCourse={virtualCourse}
        refresh={refreshMeetAndDeclarations}
      />

      {impersonateClub?.clubId && (
        <ExportEntriesModal
          meetId={meetId}
          updateVisible={setExportEntriesModalVisible}
          isVisible={exportEntriesModalVisible}
          exportClubIds={exportSelectedClubIds}
          user={user as any}
          clubId={impersonateClub.clubId}
        />
      )}
    </div>
  )
}